import React, { useState, useEffect, useMemo, useRef, useCallback, useId } from "react";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { Modal, message } from "antd";
import { Hooks, Context, Util } from "@xt/xtbase";
import {
  get_mySummary,
  get_myCurrent,
  get_cfdKline,
  get_strategies,
  get_strategyReturnTrend,
  get_strategyDetail,
  follow,
  followByCoupon,
  get_aiLabFearGreed,
  get_aiLabPnlRanking,
  get_aiLabQuantMessage,
  getAiLabQuantMessageWsSubscribeBody,
  getAiLabQuantMessageWsUrl,
  get_aiLabPositions,
} from "@/api/aicopy";
import { get_balances, get_priceCurrencyConvert } from "@/api/v4/balance";
import { getFiatListByBalance } from "@/api/v4/fiat";
import { get_convertConfigList, post_convertPreview } from "@/api/v4/convert";
import { resolveFiatOptionsForBalance, getRecommendedFiatCodesFromList } from "@/utils/fiatMarket";
import { useStore } from "@/models";
import {
  computeEstimatedSettledUsdtStr,
  computeWithdrawableProfitUsdtStr,
  parseAicopyGrossProfitRaw,
  resolveAicopyPositionId,
} from "./utils/endOrderSettledUsdt";
import {
  findAicopyPayToUsdtConfig,
  buildAicopyPayToUsdtOrder,
  validateAicopyPayToUsdtConvert,
  executeAicopyConvertToUsdtOrder,
} from "./utils/aicopyConvert";
import ConvertConfirmModal from "./components/ConvertConfirmModal";
const AicopySettlementSheet = dynamic(() => import("./components/AicopySettlementSheet"), { ssr: false });
const AicopyAssetCurveChart = dynamic(() => import("./components/AicopyAssetCurveChart"), { ssr: false });
// import WithdrawProfitModal from "./components/WithdrawProfitModal";
import OrderForm from "./components/OrderForm";
import successIcon from "assets/images/aicopy/success.png";
import { getProfitColorClass } from "./utils/colorScheme";
import { getAicopyInvestmentAmountErrorMsg } from "./utils/getInvestmentAmountErrorMsg";
import { getAicopyEffectiveMinMax, computeAicopyStrategyCardAmountBounds, isAicopyBalanceSufficientForDefaultAmount, isAicopyFiatConvertTargetSufficient } from "./utils/strategyCardAmountBounds";
import {
  AICOPY_ORDER_OPTION_VOUCHER,
  isAicopyVoucherOrderOption,
  isAicopyVoucherUseEntryQuery,
  parsePayWithVoucherQuery,
} from "./utils/aicopyOrderOptions";
import {
  AICOPY_USDC_CODE,
  formatAicopyAvailableBalanceDisplay,
  isAicopyUsdc,
  isAicopyUsdt,
  needsAicopyConvertBeforePay,
  normalizeAicopyBalanceRaw,
  parseAicopyAssetBalanceRaw,
  parseAicopyBalanceNum,
  resolveAicopyCurrentAvailableBalanceStr,
  resolveAicopyPayConvertRate,
} from "./utils/aicopyCurrencies";
import { resolveCouponTenantId } from "@/api/coupon-user";
import { fetchAicopyUsableVouchersCached, invalidateAicopyUsableVouchersCache } from "./utils/fetchAicopyUsableVouchers";
import {
  getAicopyStrategyOrderCurrencyOptions,
  resolveAicopyStrategyDefaultPayCurrency,
  resolveAicopyStrategyPayCurrencySortCtx,
} from "./utils/resolveAicopyStrategyPayCurrency";
import {
  findAicopyVoucherFollowTarget,
  scrollToAicopyStrategyCard,
} from "./utils/findAicopyVoucherFollowTarget";
import {
  getAvgDailyReturnRateFromStrategyDetail,
  buildAicopyEstimatedDailyProfitText,
} from "./utils/aicopyEstimatedDailyProfit";
import styles from "./index.module.less";
import ReturnRateChart from "./components/ReturnRateChart";
import AiLabFearGreedCard from "./components/AiLabFearGreedCard";
import AiLabJointLabCard from "./components/AiLabJointLabCard";
import AiLabPnlRankingCard from "./components/AiLabPnlRankingCard";
import OrderHistoryTab from "./components/OrderHistoryTab";
import AiWorkingStatusBadge from "./components/AiWorkingStatusBadge";
import { useQuantStrategyStatusWs } from "./utils/useQuantStrategyStatusWs";
import AicopyOrderFollowMetaRows from "./components/AicopyOrderFollowMetaRows";
import CouponPositionActionBar from "./components/CouponPositionActionBar";
import FollowSuccessModalActions from "./components/FollowSuccessModalActions";
import strategyIconAlpha10 from "assets/images/aicopy/eb3a5f42-0255-4b85-82c6-bd5462a3928b.png";
import strategyIconBuffett from "assets/images/aicopy/70257bec-d1be-475f-9255-97beeff66f82.png";
import arrowRight from "assets/images/aicopy/898172df-f017-4eff-be9d-a2b6fe27e1fa.svg";
import tagBackground from "assets/images/aicopy/7e4271f1-feec-44a0-9dcc-aeec6f8ed213.svg";
import tagIcon from "assets/images/aicopy/ae0a5b7b-f195-4586-9300-03ed4ed14813.svg";
import OrderCardCornerBadge from "./components/OrderCardCornerBadge";
import { isAicopyCouponPosition, parseCouponExpireTimeMs } from "./utils/couponPositionFields";
import eyeIcon from "assets/images/aicopy/bc1f5de1-236c-4e7a-a07c-fe7e9c55d309.svg";
import liveTickerTrendDown from "assets/images/aicopy/live-ticker-trend-down.svg";
import liveTickerTrendUp from "assets/images/aicopy/live-ticker-trend-up.svg";

const { useTranslation } = Hooks;
const { XtContext } = Context;
const { getUrl, Big, showRate } = Util;
function formatTickerPairLabel(s) {
  if (!s || typeof s !== "string") return "";
  const normalized = s
    .replace(/_cfd$/i, "")
    .replace(/([A-Z]+)(USDT|USDC|USD|BTC|ETH)$/i, "$1_$2")
    .replace(/\//g, "_")
    .toLowerCase();
  const parts = normalized.split("_").filter(Boolean);
  return parts.map((p) => p.toUpperCase()).join("/");
}

/**
 * 展示与价格 map 分组 key：统一为 btc_usdt（去掉 _cfd，仅用于界面与内存 key）。
 * 兼容 BTCUSDT、BTC/USDT、btc_usdt_cfd。
 */
function normalizeDisplaySymbol(s) {
  if (s == null || s === "") return "";
  let t = String(s).trim();
  t = t.replace(/_cfd$/i, "");
  t = t.replace(/\//g, "_");
  if (!t.includes("_") && /(USDT|USDC|USD|BTC|ETH)$/i.test(t)) {
    t = t.replace(/([A-Za-z0-9]+)(USDT|USDC|USD|BTC|ETH)$/i, "$1_$2");
  }
  return t.toLowerCase();
}

/**
 * kline HTTP 与 WebSocket 订阅用：合约行情必须带 _cfd（如 btc_usdt_cfd）。
 */
function toKlineMarketSymbol(s) {
  const base = normalizeDisplaySymbol(s);
  if (!base) return "";
  return `${base}_cfd`;
}

/** LIVE 涨跌箭头与胶囊底色（Figma 10397:34709） */
function getLiveTickerReturnDirection(returnRateText) {
  const s = String(returnRateText ?? "").trim();
  if (!s || s === "—" || s === "--") return "flat";
  const cleaned = s.replace(/,/g, "").replace(/%/g, "").replace(/\+/g, "").trim();
  const n = parseFloat(cleaned);
  if (!Number.isFinite(n)) return "flat";
  if (n < 0) return "down";
  if (n > 0) return "up";
  return "flat";
}

/** Quant 消息展示时间（ISO 等），与 WS `orderedBy: displaytime` 对齐 */

/** 解析 GET /public/ai-lab/quant-message 响应（list/total/page） */
function parseQuantMessagePayload(res) {
  let payload = null;
  if (res && typeof res.code === "number" && res.code === 0 && res.data) {
    payload = res.data;
  } else if (res && typeof res.rc === "number" && res.rc === 0) {
    payload = res.result?.data ?? res.result;
  } else {
    payload = res?.data ?? res?.result ?? res;
  }
  if (!payload || typeof payload !== "object") {
    return { list: [], total: 0, page: 1 };
  }
  const list = Array.isArray(payload.list) ? payload.list : [];
  const total = payload.total != null ? Number(payload.total) : 0;
  const page = payload.page != null ? Number(payload.page) : 1;
  return { list, total, page };
}

/** 与后端 QuantMessageVO 对齐：主键 id，兼容 messageId / message_id */
function pickQuantMessageStableId(row) {
  if (!row || typeof row !== "object") return null;
  const raw = row.id ?? row.messageId ?? row.message_id;
  if (raw == null || raw === "") return null;
  const s = String(raw).trim();
  return s.length ? s : null;
}

/** 保持数组顺序，后出现的同 id 丢弃（用于 HTTP 多页合并防重） */
function dedupeQuantMessagesByStableIdInOrder(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return rows || [];
  const seen = new Set();
  const out = [];
  for (const row of rows) {
    if (row == null || typeof row !== "object") continue;
    const k = pickQuantMessageStableId(row);
    if (k != null) {
      if (seen.has(k)) continue;
      seen.add(k);
    }
    out.push(row);
  }
  return out;
}

/** HTTP `desc=true` 多页拉取，合并为全局新→旧；返回 UI 用旧→新数组与 WS 游标（已展示的最新一条 id） */
async function fetchAllQuantMessagesForAiLabHttp(locale, strategyId) {
  const pageSize = 500;
  let page = 1;
  const allDesc = [];
  let reportedTotal = null;
  const strategyParam =
    strategyId != null && strategyId !== "" ? { strategyId: String(strategyId) } : {};
  for (;;) {
    const res = await get_aiLabQuantMessage({
      isResFull: true,
      locale,
      params: { page, pageSize, desc: true, ...strategyParam },
    });
    const { list, total } = parseQuantMessagePayload(res);
    if (reportedTotal == null && Number.isFinite(total) && total > 0) {
      reportedTotal = total;
    }
    if (!Array.isArray(list) || list.length === 0) break;
    allDesc.push(...list);
    if (list.length < pageSize) break;
    if (reportedTotal != null && allDesc.length >= reportedTotal) break;
    page += 1;
    if (page > 200) break;
  }
  const newest = allDesc[0];
  let afterMessageId;
  const newestIdStr = pickQuantMessageStableId(newest);
  if (newestIdStr != null) {
    const n = Number(newestIdStr);
    if (Number.isFinite(n)) afterMessageId = n;
  }
  const asc = dedupeQuantMessagesByStableIdInOrder([...allDesc].reverse());
  return { asc, afterMessageId };
}

function parsePositionDecimal(value) {
  if (value == null || value === "") return NaN;
  const n = parseFloat(String(value).replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : NaN;
}

/** LIVE 轮播：盈亏金额展示（千分位 + 正负号） */
function formatSignedAmountTicker(raw) {
  const n = parsePositionDecimal(raw);
  if (!Number.isFinite(n)) return "—";
  const sign = n > 0 ? "+" : "";
  const absStr = Math.abs(n).toString();
  const parts = absStr.split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const body = parts.join(".");
  if (n < 0) return `-${body}`;
  return `${sign}${body}`;
}

/**
 * 与 detail / myOrders 持仓一致：接口 `pnlRate` 为小数收益率（如 0.0233 表示 2.33%）。
 */
function formatPnlRateTickerText(raw) {
  if (raw == null || raw === "") return "—";
  const n = parseFloat(String(raw).replace(/,/g, ""));
  if (!Number.isFinite(n)) return "—";
  const pct = n * 100;
  const sign = pct > 0 ? "+" : "";
  return `${sign}${pct.toFixed(2)}%`;
}

function pickPositionPnlAmount(position) {
  if (!position || typeof position !== "object") return null;
  const v =
    position.pnlAmount ??
    position.pnl_amount ??
    position.unrealizedPnl ??
    position.unrealized_pnl ??
    position.unrealizedPNL;
  return v;
}

function pickPositionPnlRate(position) {
  if (!position || typeof position !== "object") return null;
  return position.pnlRate ?? position.pnl_rate ?? position.returnRate ?? position.return_rate;
}

/**
 * 顶部 LIVE 轮播：每条 /public/ai-lab/positions 一行。
 * 展示：币对 + LONG/SHORT + PNL + 接口字段 pnlAmount + pnlRate（不再用行情价自行推算收益率）。
 */
function buildLiveTickerRows(positionList) {
  if (!Array.isArray(positionList) || positionList.length === 0) return [];

  return positionList
    .map((position, idx) => {
      const symbol = normalizeDisplaySymbol(position?.symbol);
      if (!symbol) return null;

      const direction = String(position?.direction ?? position?.side ?? "").toUpperCase();
      const directionLabel = direction === "SHORT" ? "SHORT" : "LONG";
      const positionKey = position?.id != null ? String(position.id) : `${symbol}-${idx}`;
      const pnlAmountText = formatSignedAmountTicker(pickPositionPnlAmount(position));
      const pnlRateText = formatPnlRateTickerText(pickPositionPnlRate(position));

      return {
        s: symbol,
        positionKey,
        directionLabel,
        pnlAmountText,
        pnlRateText,
      };
    })
    .filter(Boolean);
}

/** 未翻译时（返回 key）用英文兜底，避免界面出现 aicopy.xxx */
function aicopyT(msg, en) {
  return msg && typeof msg === "string" && !msg.startsWith("aicopy.") ? msg : en;
}

/** 规则弹窗：按空行分段，首行为章节标题，其余为正文 */
function parseAssetRulesSections(content) {
  if (!content || typeof content !== "string") return [];
  return content
    .split(/\n\n+/)
    .map((block) => {
      const lines = block.split("\n").map((l) => l.trim()).filter(Boolean);
      if (!lines.length) return null;
      return { title: lines[0], bodyLines: lines.slice(1) };
    })
    .filter(Boolean);
}

/** 策略列表接口 return90d：与 totalReturnRate 展示口径一致（数值 + %） */
function formatStrategyReturn90dPercent(strategy) {
  const raw = strategy?.return90d ?? strategy?.return_90d;
  if (raw == null || String(raw).trim() === "") return { text: "--", colorValue: null };
  const n = parseFloat(String(raw).replace(/,/g, ""));
  if (!Number.isFinite(n)) return { text: "--", colorValue: null };
  const s = String(raw).replace(/,/g, "");
  const text = n > 0 ? `+${s}%` : n < 0 ? `${s}%` : "0%";
  return { text, colorValue: s };
}

function formatEstimatedDailyAmountForDisplay(n) {
  if (!Number.isFinite(n) || n < 0) return null;
  const s = n.toFixed(6).replace(/\.?0+$/, "");
  return s || "0";
}

function AiCopy() {
  const router = useRouter();
  const orderCountArrowGradId = `aicopyTabCountGrad${useId().replace(/:/g, "")}`;
  const t = useTranslation();
  const [appState] = React.useContext(XtContext);
  const { loginStatus, userInfo } = appState;
  const { fund } = useStore();
  const [activeTab, setActiveTab] = useState("aiStrategy"); // 默认第一个 tab：Strategy Copy Trading
  const [isAssetVisible, setIsAssetVisible] = useState(true);
  const [assetChartExpanded, setAssetChartExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [assetData, setAssetData] = useState({
    totalAssets: "0",
    totalProfit: "0",
    totalReturnRate: "0",
    currentFollowCount: 0,
    todayProfit: "0",
    yesterdayProfit: "0",
  });
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [strategies, setStrategies] = useState([]);
  const [strategiesLoading, setStrategiesLoading] = useState(false);
  const strategiesLoadingRef = useRef(false);
  const [strategiesHasMore, setStrategiesHasMore] = useState(true);
  const [strategiesOffset, setStrategiesOffset] = useState(0);
  const [settlementSheet, setSettlementSheet] = useState({
    open: false,
    mode: null,
    positionId: null,
    order: null,
  });
  const [strategyReturnTrends, setStrategyReturnTrends] = useState({}); // 存储每个策略的 returnTrend30 数据
  const [expandedDescId, setExpandedDescId] = useState(null); // 展开的策略描述 id，用于 desc 折叠
  const myOrdersStrategyIds = useMemo(
    () => orders.map((o) => o.strategyId).filter((id) => id != null && id !== ""),
    [orders],
  );
  const aiStrategyIds = useMemo(
    () => strategies.map((s) => s.strategyId).filter((id) => id != null && id !== ""),
    [strategies],
  );
  const quantWsStrategyIds = useMemo(() => {
    if (activeTab === "myOrders") return myOrdersStrategyIds;
    if (activeTab === "aiStrategy") return aiStrategyIds;
    return [];
  }, [activeTab, myOrdersStrategyIds, aiStrategyIds]);
  const { getStatusLabel: getQuantStrategyStatusLabel } = useQuantStrategyStatusWs(
    activeTab === "myOrders" || activeTab === "aiStrategy",
    quantWsStrategyIds,
    router.locale,
  );
  // 策略跟单 tab：余额与策略限额、表单状态
  const [availableBalance, setAvailableBalance] = useState("0");
  const [usdcAvailableBalance, setUsdcAvailableBalance] = useState("0");
  const [strategyDetails, setStrategyDetails] = useState({}); // { [strategyId]: { minAmountPerUser, maxAmountPerUser, avgDailyReturnRate?（接口百分点，计算见 getAvgDailyReturnRateFromStrategyDetail） } }
  const [formStateByStrategy, setFormStateByStrategy] = useState({}); // { [strategyId]: { amount: '', showDropdown: false } }
  const [submittingStrategyId, setSubmittingStrategyId] = useState(null);
  const [showFollowSuccessModal, setShowFollowSuccessModal] = useState(false);
  const [followSuccessDisplay, setFollowSuccessDisplay] = useState(null); // { amount, currency }
  // 策略跟单 tab 法币（与首页/市场一致：fiatCurrencyIds + IP + 有余额）
  const [fiatOptions, setFiatOptions] = useState([]);
  const [fiatBalanceMap, setFiatBalanceMap] = useState({});
  const [primaryFiatCode, setPrimaryFiatCode] = useState(null);
  const [fiatMetaReady, setFiatMetaReady] = useState(false);
  const [balancesReady, setBalancesReady] = useState(false);
  const [hasUsableVouchers, setHasUsableVouchers] = useState(false);
  const [usableVouchers, setUsableVouchers] = useState([]);
  const [vouchersMetaReady, setVouchersMetaReady] = useState(false);
  const [fiatRatesMap, setFiatRatesMap] = useState({}); // { [fiatCode]: rate } 1 USDT = rate 法币
  const [selectedCurrencyByStrategy, setSelectedCurrencyByStrategy] = useState({}); // { [strategyId]: currency }
  const [followErrorDepositCurrency, setFollowErrorDepositCurrency] = useState("USDT");
  // 法币兑换确认弹窗（与加仓页一致）
  const [convertConfigList, setConvertConfigList] = useState([]);
  const [showConvertConfirmModal, setShowConvertConfirmModal] = useState(false);
  const [convertPreviewData, setConvertPreviewData] = useState(null);
  const [convertPreviewLoading, setConvertPreviewLoading] = useState(false);
  const [showFollowErrorModal, setShowFollowErrorModal] = useState(false);
  const [followErrorModalMessage, setFollowErrorModalMessage] = useState("");
  const [followErrorIsInsufficientFunds, setFollowErrorIsInsufficientFunds] = useState(false); // true 时弹窗显示「取消」+「去充值」
  const [showAssetRulesModal, setShowAssetRulesModal] = useState(false);
  const [convertModalSourceAmount, setConvertModalSourceAmount] = useState("0"); // 弹窗展示用法币金额
  const [convertConfirmDirection, setConvertConfirmDirection] = useState("fiatToUsdt");
  const [convertModalReceiveCurrency, setConvertModalReceiveCurrency] = useState("USDT");
  const pendingConvertRef = useRef(null); // { mode:'follow', ... }
  const defaultAmountSetForStrategiesRef = useRef(new Set()); // 策略列表里已设过默认最小金额的 strategyId
  const prevCurrencyByStrategyRef = useRef({}); // 检测各策略币种切换，切换时按新币种最小金额重新填入
  const prevBalanceRefreshTabRef = useRef("aiStrategy"); // 策略列表 ↔ 我的跟单 切换时刷新余额
  const [liveTickers, setLiveTickers] = useState([]);
  const [aiLabFearGreed, setAiLabFearGreed] = useState(null);
  const [aiLabFearGreedLoading, setAiLabFearGreedLoading] = useState(false);
  const [aiLabPnlRanking, setAiLabPnlRanking] = useState([]);
  const [aiLabPnlRankingLoading, setAiLabPnlRankingLoading] = useState(false);
  const [aiLabPnlAiType, setAiLabPnlAiType] = useState("");
  const [aiLabHistory, setAiLabHistory] = useState([]);
  /** Quant：当前筛选的 AI 策略 ID（HTTP / WS strategyId） */
  const [aiLabQuantStrategyId, setAiLabQuantStrategyId] = useState("");
  /** Quant：HTTP 全量拉取进行中（WS 在 HTTP 完成后建立） */
  const [aiLabQuantMessagesLoading, setAiLabQuantMessagesLoading] = useState(false);
  const liveTickerPricesRef = useRef({});
  const liveTickerPositionsRef = useRef([]);
  /** 仅响应 URL 中带「用券下单」参数的一次性应用，避免与后续用户手动切币种打架 */
  const payWithVoucherQueryAppliedRef = useRef(false);
  /** 手动进入 /aicopy：已尝试自动用券并滚动（或已放弃） */
  const voucherAutoFollowAppliedRef = useRef(false);
  const voucherAutoFollowInFlightRef = useRef(false);
  const voucherAutoFollowLoadMoreAttemptsRef = useRef(0);
  const voucherAutoFollowEntryKeyRef = useRef("");
  const userPayCurrencyTouchedByStrategyRef = useRef({});
  const defaultPayCurrencyAppliedByStrategyRef = useRef(new Set());
  const loadStrategiesRef = useRef(null);
  /** 券页「去跟单」带 userCouponId 时，OrderForm 内优先选中该券 */
  const [preferredVoucherUserCouponId, setPreferredVoucherUserCouponId] = useState(null);
  /** 用券下单：按策略 ID 记录选中券（多卡片各自 OrderForm，避免互相 cb(null) 覆盖） */
  const [selectedVoucherByStrategy, setSelectedVoucherByStrategy] = useState({});
  const selectedVoucherByStrategyRef = useRef({});

  useEffect(() => {
    if (!router.isReady) return;
    const raw = router.query.userCouponId ?? router.query.user_coupon_id;
    if (raw == null || raw === "") {
      setPreferredVoucherUserCouponId(null);
      return;
    }
    setPreferredVoucherUserCouponId(Array.isArray(raw) ? String(raw[0]) : String(raw));
  }, [router.isReady, router.query.userCouponId, router.query.user_coupon_id]);

  // 外链（券 Use 等）带 payWithVoucher 时默认「用券下单」；手动进入 /aicopy 由下方自动用券逻辑处理
  useEffect(() => {
    if (!router.isReady) return;
    const wantVoucher = parsePayWithVoucherQuery(router.query);
    if (!wantVoucher) {
      payWithVoucherQueryAppliedRef.current = false;
      return;
    }
    if (payWithVoucherQueryAppliedRef.current) return;
    payWithVoucherQueryAppliedRef.current = true;
    setHasUsableVouchers(true);
  }, [router.isReady, router.query]);

  // 每次手动进入 /aicopy（无券 Use 外链参数）重置一次性自动用券 / 默认币种状态
  useEffect(() => {
    if (!router.isReady) return;
    if (isAicopyVoucherUseEntryQuery(router.query)) return;
    const entryKey = router.asPath || router.pathname || "/aicopy";
    if (voucherAutoFollowEntryKeyRef.current === entryKey) return;
    voucherAutoFollowEntryKeyRef.current = entryKey;
    voucherAutoFollowAppliedRef.current = false;
    voucherAutoFollowLoadMoreAttemptsRef.current = 0;
    voucherAutoFollowInFlightRef.current = false;
    userPayCurrencyTouchedByStrategyRef.current = {};
    defaultPayCurrencyAppliedByStrategyRef.current = new Set();
    setSelectedCurrencyByStrategy({});
    setUsableVouchers([]);
    setVouchersMetaReady(false);
  }, [router.isReady, router.asPath, router.pathname, router.query]);

  // 格式化数字（千分位），与 detail/myOrders 一致
  const formatNumber = (num) => {
    if (!num && num !== 0) return "0";
    const numStr = typeof num === "string" ? num : num.toString();
    const parts = numStr.split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.join(".");
  };

  const formatApiAmount = (str, currency) => {
    if (str == null || str === "") return "";
    const s = String(str).replace(/,/g, "").trim();
    if (!s || !/^-?\d+(\.\d+)?$/.test(s)) return "";
    const isFiat = currency && !isAicopyUsdt(currency) && !isAicopyUsdc(currency);
    if (isFiat) {
      const n = parseFloat(s);
      if (!Number.isFinite(n)) return "";
      return n.toFixed(2).replace(/\.?0+$/, "") || "0";
    }
    return s.replace(/\.?0+$/, "") || "0";
  };
  // 获取单条持仓最新价（kline 接口，与 detail/myOrders 一致）
  const getLatestPriceBySymbol = async (symbol) => {
    const s = toKlineMarketSymbol(symbol);
    if (!s) return "";
    try {
      const res = await get_cfdKline({
        isResFull: true,
        params: { symbol: s, interval: "1m", limit: 1, version: 2 },
      });
      const raw = res?.result ?? res;
      const items = Array.isArray(raw?.items) ? raw.items : Array.isArray(raw) ? raw : [];
      const close = items[0]?.c;
      return close != null && close !== "" ? String(close) : "";
    } catch (err) {
      return "";
    }
  };

  // 获取总览数据
  useEffect(() => {
    loadSummary();
    loadMyCurrent();
    loadStrategies();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadLive = async () => {
      try {
        const res = await get_aiLabPositions({ isResFull: true });
        const list = Array.isArray(res?.data) ? res.data : Array.isArray(res?.result) ? res.result : Array.isArray(res) ? res : [];
        if (cancelled || !Array.isArray(list)) return;
        const picked = buildLiveTickerRows(list);
        if (!cancelled) setLiveTickers(picked);
      } catch {
        if (!cancelled) setLiveTickers([]);
      }
    };
    loadLive();
    const interval = setInterval(loadLive, 30000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  /** 资产区「一键跟单」胶囊：展示策略列表中 90 日收益率（return90d）最高的一条 */
  const topStrategyByReturn = useMemo(() => {
    if (!Array.isArray(strategies) || strategies.length === 0) return null;
    let best = null;
    let bestNum = -Infinity;
    for (const s of strategies) {
      const raw = s?.return90d ?? s?.return_90d ?? s?.totalReturnRate;
      const n = parseFloat(raw);
      const val = Number.isFinite(n) ? n : -Infinity;
      if (val > bestNum) {
        bestNum = val;
        best = s;
      }
    }
    return best;
  }, [strategies]);

  /** AI Lab Quant 模型筛选：默认选中策略列表第一项 */
  useEffect(() => {
    if (activeTab !== "aiLab") return;
    if (!Array.isArray(strategies) || strategies.length === 0) return;
    setAiLabQuantStrategyId((prev) => {
      if (prev && strategies.some((s) => s?.strategyId != null && String(s.strategyId) === String(prev))) {
        return prev;
      }
      const firstId = strategies[0]?.strategyId;
      return firstId != null && firstId !== "" ? String(firstId) : prev;
    });
  }, [activeTab, strategies]);

  const aiLabQuantStrategyOptions = useMemo(() => {
    if (!Array.isArray(strategies)) return [];
    return strategies
      .filter((s) => s?.strategyId != null && s.strategyId !== "")
      .map((s) => ({
        strategyId: String(s.strategyId),
        label: (s.strategyName || "").trim() || String(s.strategyId),
      }));
  }, [strategies]);

  // AI Lab tab：恐惧贪婪 + PNL 排行（Quant 消息：HTTP 全量 + WS 增量）
  useEffect(() => {
    if (activeTab !== "aiLab") return;
    let cancelled = false;
    const parseFearPayload = (res) => {
      let payload = null;
      if (res && typeof res.code === "number" && res.code === 0 && res.data) {
        payload = res.data;
      } else if (res && typeof res.rc === "number" && res.rc === 0) {
        payload = res.result?.data ?? res.result;
      } else {
        const root = res?.data ?? res?.result ?? res;
        payload = root?.data ?? root;
      }
      return payload && payload.current != null ? payload : null;
    };
    const load = async () => {
      setAiLabFearGreedLoading(true);
      try {
        const fearRes = await get_aiLabFearGreed({ isResFull: true });
        if (cancelled) return;
        setAiLabFearGreed(parseFearPayload(fearRes));
      } catch {
        if (!cancelled) setAiLabFearGreed(null);
      } finally {
        if (!cancelled) setAiLabFearGreedLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [activeTab, router.locale]);

  useEffect(() => {
    if (activeTab !== "aiLab") return;
    let cancelled = false;
    const parseRankingList = (res) => {
      if (!res) return [];
      if (typeof res.code === "number" && res.code === 0 && Array.isArray(res.data)) return res.data;
      if (typeof res.rc === "number" && res.rc === 0) {
        const r = res.result;
        if (Array.isArray(r)) return r;
        if (Array.isArray(r?.data)) return r.data;
        return [];
      }
      const root = res.data ?? res.result;
      if (Array.isArray(root)) return root;
      if (Array.isArray(root?.data)) return root.data;
      return [];
    };
    const loadRanking = async () => {
      setAiLabPnlRanking([]);
      setAiLabPnlRankingLoading(true);
      try {
        const rankRes = await get_aiLabPnlRanking({
          isResFull: true,
          params: aiLabPnlAiType ? { aiType: aiLabPnlAiType } : {},
        });
        if (cancelled) return;
        setAiLabPnlRanking(parseRankingList(rankRes));
      } catch {
        if (!cancelled) setAiLabPnlRanking([]);
      } finally {
        if (!cancelled) setAiLabPnlRankingLoading(false);
      }
    };
    loadRanking();
    return () => {
      cancelled = true;
    };
  }, [activeTab, aiLabPnlAiType]);

  // AI Quant：先 HTTP 分页拉全量（界面旧→新），再 WS subscribe(strategyId + afterMessageId) 接尾巴与调度增量
  useEffect(() => {
    if (activeTab !== "aiLab") return undefined;
    if (!aiLabQuantStrategyId) return undefined;
    if (typeof WebSocket === "undefined") return undefined;

    let cancelled = false;
    let ws;
    let onOpen;
    let onMessage;

    const teardown = () => {
      cancelled = true;
      try {
        if (ws) {
          if (onOpen) ws.removeEventListener("open", onOpen);
          if (onMessage) ws.removeEventListener("message", onMessage);
          ws.close();
        }
      } catch {
        /* ignore */
      }
    };

    (async () => {
      setAiLabHistory([]);
      setAiLabQuantMessagesLoading(true);
      let afterMessageId;
      let asc = [];
      try {
        const fetched = await fetchAllQuantMessagesForAiLabHttp(router.locale, aiLabQuantStrategyId);
        asc = fetched.asc || [];
        afterMessageId = fetched.afterMessageId;
        if (cancelled) return;
        setAiLabHistory(asc);
      } catch {
        if (!cancelled) setAiLabHistory([]);
        asc = [];
      } finally {
        if (!cancelled) setAiLabQuantMessagesLoading(false);
      }
      if (cancelled) return;

      const seenQuantMessageIds = new Set();
      for (const row of asc) {
        const k = pickQuantMessageStableId(row);
        if (k) seenQuantMessageIds.add(k);
      }

      const appendWsQuantMessagesIfNew = (incoming) => {
        if (!Array.isArray(incoming) || incoming.length === 0) return;
        const additions = [];
        for (const row of incoming) {
          if (row == null || typeof row !== "object") continue;
          const k = pickQuantMessageStableId(row);
          if (k != null) {
            if (seenQuantMessageIds.has(k)) continue;
            seenQuantMessageIds.add(k);
          }
          additions.push(row);
        }
        if (additions.length === 0) return;
        setAiLabHistory((prev) => [...prev, ...additions]);
      };

      try {
        ws = new WebSocket(getAiLabQuantMessageWsUrl());
      } catch {
        return;
      }

      onOpen = () => {
        try {
          ws.send(
            JSON.stringify(
              getAiLabQuantMessageWsSubscribeBody(router.locale, {
                strategyId: aiLabQuantStrategyId,
                afterMessageId,
              }),
            ),
          );
        } catch {
          /* ignore */
        }
      };

      onMessage = (event) => {
        let data;
        try {
          data = JSON.parse(event.data);
        } catch {
          return;
        }
        if (!data || typeof data !== "object") return;
        if (data.type === "subscribe_ack" || data.type === "heartbeat") return;
        if (
          (data.type === "quant_message_item" || typeof data.snapshot === "boolean") &&
          data.message &&
          typeof data.message === "object"
        ) {
          appendWsQuantMessagesIfNew([data.message]);
          return;
        }
        if (Array.isArray(data.list) && data.total != null && data.page != null) {
          appendWsQuantMessagesIfNew(data.list);
          return;
        }
        if (Array.isArray(data)) {
          appendWsQuantMessagesIfNew(data);
        }
      };

      ws.addEventListener("open", onOpen);
      ws.addEventListener("message", onMessage);
    })();

    return teardown;
  }, [activeTab, router.locale, aiLabQuantStrategyId]);

  // 监听策略列表滚动，实现滑动分页
  useEffect(() => {
    if (activeTab !== "aiStrategy") return;

    const handleScroll = () => {
      // 获取窗口滚动信息
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      // 滚动到底部附近（距离底部100px）时加载更多
      if (documentHeight - scrollTop - windowHeight < 100 && !strategiesLoading && strategiesHasMore) {
        loadStrategies(true);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [activeTab, strategiesLoading, strategiesHasMore]);

  const loadSummary = async (opts) => {
    const silent = opts?.silent;
    if (!silent) setLoading(true);
    try {
      const response = await get_mySummary({ isResFull: true });

      const fromMy = response.rc === 0 && response.result;
      if (response.rc !== 0) {
        console.error("获取跟单总览失败:", response.mc);
      }

      const totalAssets = fromMy ? fromMy.totalAssets || "0" : "0";
      const totalProfit = fromMy ? fromMy.totalProfit || "0" : "0";

      const fmtMyPnl = (raw) => {
        if (raw == null || String(raw).trim() === "") return "0";
        return formatNumber(String(raw).replace(/,/g, ""));
      };

      setAssetData({
        totalAssets,
        totalProfit,
        totalReturnRate: fromMy?.totalReturnRate || "0",
        currentFollowCount: fromMy?.currentFollowCount || 0,
        todayProfit: fromMy ? fmtMyPnl(fromMy.todayProfit) : "0",
        yesterdayProfit: fromMy ? fmtMyPnl(fromMy.yesterdayProfit) : "0",
      });
    } catch (error) {
      console.error("获取跟单总览接口调用失败", error);
      setAssetData({
        totalAssets: "0",
        totalProfit: "0",
        totalReturnRate: "0",
        currentFollowCount: 0,
        todayProfit: "0",
        yesterdayProfit: "0",
      });
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // 获取我当前跟随的策略列表（拉持仓与价格逻辑与历史版本一致；卡片展示仍为接口已实现收益/收益率）
  const loadMyCurrent = async (opts) => {
    const silent = opts?.silent;
    if (!silent) setOrdersLoading(true);
    try {
      const response = await get_myCurrent({
        isResFull: true,
      });

      if (response.rc !== 0 || !Array.isArray(response.result)) {
        console.error("获取我的跟单列表失败:", response.mc);
        setOrders([]);
        return;
      }

      const rawOrders = response.result || [];
      const enrichedOrders = rawOrders.map((item) => {
          const profitRawNum = parseFloat(String(item.profit ?? "").replace(/,/g, ""));
          const profitRaw = Number.isFinite(profitRawNum) ? profitRawNum : 0;
          const profitStr = item.profit != null ? formatNumber(String(item.profit)) : "";
          const grossProfitRaw = parseAicopyGrossProfitRaw(item);
          const totalReturnRateStr =
            item.totalReturnRate != null && Number.isFinite(parseFloat(item.totalReturnRate))
              ? (() => {
                  const n = parseFloat(item.totalReturnRate) * 100;
                  return (n >= 0 ? "+" : "") + n.toFixed(2) + "%";
                })()
              : "0.00%";
          const positionId = resolveAicopyPositionId(item);

          return {
            ...item,
            positionId,
            profit: profitStr,
            profitRaw,
            totalReturnRate: totalReturnRateStr,
            grossProfitRaw,
          };
        });

      setOrders(enrichedOrders);
    } catch (error) {
      console.error("获取我的跟单列表接口调用失败", error);
      setOrders([]);
    } finally {
      if (!silent) setOrdersLoading(false);
    }
  };

  const loadSummaryRef = useRef(loadSummary);
  const loadMyCurrentRef = useRef(loadMyCurrent);
  loadSummaryRef.current = loadSummary;
  loadMyCurrentRef.current = loadMyCurrent;

  useEffect(() => {
    if (!router.isReady) return;
    const raw = router.query.tab;
    const tab = raw == null || raw === "" ? "" : Array.isArray(raw) ? String(raw[0]) : String(raw);
    if (tab === "myOrders") {
      setActiveTab("myOrders");
      loadMyCurrentRef.current?.();
    }
  }, [router.isReady, router.query.tab]);

  // 总利润来自 HTTP my/summary，与 kline 行情 socket 无关；登录后轮询刷新，避免长期停留页面数据陈旧
  useEffect(() => {
    if (!loginStatus) return undefined;
    const id = setInterval(() => {
      loadSummaryRef.current({ silent: true });
      loadMyCurrentRef.current({ silent: true });
    }, 30000);
    return () => clearInterval(id);
  }, [loginStatus]);

  // 获取 USDT 余额（策略跟单 tab 内嵌 OrderForm 用）
  const loadBalance = async () => {
    if (!loginStatus) {
      setAvailableBalance("0");
      setUsdcAvailableBalance("0");
      setBalancesReady(false);
      return;
    }
    try {
      const response = await get_balances({
        params: { currencies: "USDT,USDC" },
        isResFull: true,
      });
      const result = response.result;
      setAvailableBalance(parseAicopyAssetBalanceRaw(result, "USDT"));
      setUsdcAvailableBalance(parseAicopyAssetBalanceRaw(result, AICOPY_USDC_CODE));
    } catch (e) {
      setAvailableBalance("0");
      setUsdcAvailableBalance("0");
    } finally {
      setBalancesReady(true);
    }
  };

  // 刷新法币余额（下单成功后立即更新，避免连续下单时仍显示旧余额）
  const loadFiatBalance = useCallback(async () => {
    if (!loginStatus || fiatOptions.length === 0) return;
    try {
      const balanceRes = await getFiatListByBalance({
        params: { pageIndex: 1, pageSize: 100 },
        isAutoPopError: false,
      });
      const balanceData = balanceRes?.result ?? balanceRes?.data ?? balanceRes;
      const items = Array.isArray(balanceData) ? balanceData : balanceData?.items ?? balanceData?.list ?? [];
      const nextFiatBalanceMap = {};
      fiatOptions.forEach((code) => {
        const matched = items.find((item) => (item.coinName ?? item.currency ?? "").toUpperCase() === code);
        nextFiatBalanceMap[code] = matched
          ? normalizeAicopyBalanceRaw(matched.availableAmount ?? 0)
          : "0";
      });
      setFiatBalanceMap(nextFiatBalanceMap);
    } catch (e) {}
  }, [loginStatus, fiatOptions]);

  // 策略列表加载后拉取每个策略的 min/max 金额（供 OrderForm 门槛用）
  useEffect(() => {
    if (!strategies.length) return;
    let cancelled = false;
    const ids = strategies.map((s) => s.strategyId).filter(Boolean);
    Promise.all(
      ids.map((strategyId) =>
        get_strategyDetail(strategyId, { isResFull: true }).then((res) => ({
          strategyId,
          detail: res.rc === 0 && res.result ? res.result : null,
        }))
      )
    ).then((results) => {
      if (cancelled) return;
      const details = {};
      results.forEach(({ strategyId, detail }) => {
        if (detail) {
          details[strategyId] = {
            minAmountPerUser: detail.minAmountPerUser,
            maxAmountPerUser: detail.maxAmountPerUser,
            avgDailyReturnRate: detail.avgDailyReturnRate ?? detail.avg_daily_return_rate,
          };
        }
      });
      setStrategyDetails((prev) => ({ ...prev, ...details }));
    });
    return () => {
      cancelled = true;
    };
  }, [strategies]);

  useEffect(() => {
    loadBalance();
    const t = setInterval(loadBalance, 5000);
    return () => clearInterval(t);
  }, [loginStatus]);

  // 每次切回策略列表都刷新可用余额，避免在其它 tab 提取收益/结束跟单后展示旧资产。
  useEffect(() => {
    if (!loginStatus) return;
    const prev = prevBalanceRefreshTabRef.current;
    if (prev === activeTab) return;
    prevBalanceRefreshTabRef.current = activeTab;
    if (activeTab === "aiStrategy") {
      loadBalance();
      loadFiatBalance();
    }
  }, [activeTab, loginStatus, loadFiatBalance]);

  // 法币展示直接跟随推荐币种缓存，旧的余额/IP 二次判断不再参与法币决策。
  useEffect(() => {
    if (!loginStatus) {
      setFiatOptions([]);
      setFiatBalanceMap({});
      setPrimaryFiatCode(null);
      setFiatMetaReady(false);
      return;
    }
    let cancelled = false;
    setFiatMetaReady(false);
    const run = async () => {
      try {
        const balanceRes = await getFiatListByBalance({
          params: { pageIndex: 1, pageSize: 100 },
          isAutoPopError: false,
        });
        if (cancelled) return;
        const balanceData = balanceRes?.result ?? balanceRes?.data ?? balanceRes;
        const items = Array.isArray(balanceData) ? balanceData : balanceData?.items ?? balanceData?.list ?? [];
        const { options, primaryFiat } = await resolveFiatOptionsForBalance({
          fiatCurrencyIds: userInfo?.fiatCurrencyIds,
        });
        if (cancelled) return;
        const fiatCodes = (options || []).filter((code) => code !== "USDT");
        if (fiatCodes.length > 0 && primaryFiat && primaryFiat !== "USDT") {
          const nextFiatBalanceMap = {};
          fiatCodes.forEach((code) => {
            const matched = items.find((item) => (item.coinName ?? item.currency ?? "").toUpperCase() === code);
            nextFiatBalanceMap[code] = matched
              ? normalizeAicopyBalanceRaw(matched.availableAmount ?? 0)
              : "0";
          });
          setFiatOptions(fiatCodes);
          setFiatBalanceMap(nextFiatBalanceMap);
          setPrimaryFiatCode(primaryFiat);
        } else {
          setFiatOptions([]);
          setFiatBalanceMap({});
          setPrimaryFiatCode(null);
        }
      } catch (e) {
        if (!cancelled) {
          setFiatOptions([]);
          setFiatBalanceMap({});
          setPrimaryFiatCode(null);
        }
      } finally {
        if (!cancelled) setFiatMetaReady(true);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [loginStatus, userInfo?.fiatCurrencyIds]);

  // 各策略默认下单币种：该策略有可用券 > 法币有余额 > USDT > USDC > 法币0 > USDT（用户手动切换某策略后不再覆盖该策略）
  useEffect(() => {
    if (!router.isReady || !loginStatus) return;
    if (!fiatMetaReady || !balancesReady || !vouchersMetaReady) return;
    if (!strategies.length) return;

    const wantVoucherFromUrl = parsePayWithVoucherQuery(router.query);
    const preferredFromUrl =
      wantVoucherFromUrl && preferredVoucherUserCouponId ? preferredVoucherUserCouponId : null;

    setSelectedCurrencyByStrategy((prev) => {
      const next = { ...prev };
      let changed = false;
      strategies.forEach((strategy) => {
        const sid = strategy.strategyId;
        if (sid == null || sid === "") return;
        if (userPayCurrencyTouchedByStrategyRef.current[sid]) return;
        const detail = strategyDetails[sid];
        if (detail?.minAmountPerUser == null) return;
        if (defaultPayCurrencyAppliedByStrategyRef.current.has(sid)) return;

        const currency = resolveAicopyStrategyDefaultPayCurrency({
          vouchers: usableVouchers,
          strategyDetail: detail,
          availableBalance,
          preferredVoucherUserCouponId: preferredFromUrl,
          fiatOptions,
          fiatBalanceMap,
          usdtBalance: availableBalance,
          usdcBalance: usdcAvailableBalance,
          primaryFiat: primaryFiatCode,
        });

        defaultPayCurrencyAppliedByStrategyRef.current.add(sid);
        if (prev[sid] !== currency) {
          next[sid] = currency;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [
    router.isReady,
    router.query,
    loginStatus,
    fiatMetaReady,
    balancesReady,
    vouchersMetaReady,
    strategies,
    strategyDetails,
    fiatOptions,
    fiatBalanceMap,
    primaryFiatCode,
    availableBalance,
    usdcAvailableBalance,
    preferredVoucherUserCouponId,
    usableVouchers,
  ]);

  const strategyPayCurrencySortCtx = useMemo(
    () => ({
      fiatOptions,
      fiatBalanceMap,
      usdtBalance: availableBalance,
      usdcBalance: usdcAvailableBalance,
      hasUsableVouchers,
    }),
    [fiatOptions, fiatBalanceMap, availableBalance, usdcAvailableBalance, hasUsableVouchers]
  );

  // 可用券列表（策略列表各卡片独立判断是否可用券）
  useEffect(() => {
    if (!loginStatus) {
      setUsableVouchers([]);
      setHasUsableVouchers(false);
      setVouchersMetaReady(true);
      return;
    }
    const userId = userInfo?.userId;
    const tenantId = resolveCouponTenantId(appState);
    if (userId == null || userId === "" || tenantId == null) {
      setUsableVouchers([]);
      setHasUsableVouchers(false);
      setVouchersMetaReady(true);
      return;
    }
    let cancelled = false;
    setVouchersMetaReady(false);
    fetchAicopyUsableVouchersCached({ tenantId, userId }).then(({ ok, items }) => {
      if (cancelled) return;
      const list = ok && items?.length ? items : [];
      setUsableVouchers(list);
      setHasUsableVouchers(list.length > 0);
      setVouchersMetaReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [loginStatus, userInfo?.userId, appState]);

  // 法币汇率：预加载所有法币选项（各策略可能选中不同法币）
  useEffect(() => {
    const fiatCodes = (fiatOptions || []).filter(
      (code) => !isAicopyUsdt(code) && !isAicopyUsdc(code) && !isAicopyVoucherOrderOption(code)
    );
    if (!fiatCodes.length) {
      setFiatRatesMap({});
      return;
    }
    let cancelled = false;
    Promise.all(
      fiatCodes.map(async (code) => {
        try {
          const res = await get_priceCurrencyConvert({
            params: { converts: `usdt,${code.toLowerCase()}` },
            isAutoPopError: false,
          });
          const data = res?.result ?? res?.data ?? res;
          let r = null;
          if (data?.usdt?.[code.toLowerCase()]) {
            r = parseFloat(data.usdt[code.toLowerCase()]);
          } else if (data?.[code.toLowerCase()]?.usdt) {
            r = 1 / parseFloat(data[code.toLowerCase()].usdt);
          }
          return [code, r];
        } catch {
          return [code, null];
        }
      })
    ).then((results) => {
      if (cancelled) return;
      const next = {};
      results.forEach(([code, r]) => {
        if (r != null && Number.isFinite(r)) next[code] = r;
      });
      setFiatRatesMap(next);
    });
    return () => {
      cancelled = true;
    };
  }, [fiatOptions]);

  // 法币兑换配置（USDC 支付也需 convert 配置；不依赖用户是否有法币钱包）
  useEffect(() => {
    let cancelled = false;
    get_convertConfigList({ isAutoPopError: false })
      .then((res) => {
        if (cancelled) return;
        const raw = res?.result ?? res?.data ?? res;
        setConvertConfigList(Array.isArray(raw) ? raw : []);
      })
      .catch(() => {
        if (!cancelled) setConvertConfigList([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // 策略列表默认下单金额：各策略按自身选中币种与汇率计算；进页时填入最小金额，切换币种时按新币种最小金额重新填入
  useEffect(() => {
    if (!strategies.length) return;

    const toSet = [];
    strategies.forEach((strategy) => {
      const sid = strategy.strategyId;
      const detail = strategyDetails[sid];
      if (detail?.minAmountPerUser == null) return;

      const currency = selectedCurrencyByStrategy[sid] ?? "USDT";
      if (
        !isAicopyUsdt(currency) &&
        !isAicopyUsdc(currency) &&
        !isAicopyVoucherOrderOption(currency)
      ) {
        const fiatRate = fiatRatesMap[currency];
        if (fiatRate == null || !Number.isFinite(fiatRate)) return;
      }

      const isVoucherPayStrategy = isAicopyVoucherOrderOption(currency);
      const currentAvailableBalanceStr = resolveAicopyCurrentAvailableBalanceStr({
        currency,
        availableBalance,
        usdcAvailableBalance,
        fiatBalanceMap,
        isVoucherPay: isVoucherPayStrategy,
      });
      const currentAvailableNum = parseAicopyBalanceNum(currentAvailableBalanceStr);
      const currencyChanged = prevCurrencyByStrategyRef.current[sid] !== currency;
      if (currencyChanged) prevCurrencyByStrategyRef.current[sid] = currency;

      const convertCfg = findAicopyPayToUsdtConfig(convertConfigList, currency);
      const rate = resolveAicopyPayConvertRate({
        currency,
        fiatRate: fiatRatesMap[currency],
        convertConfig: convertCfg,
      });

      const minUsdt = Number(detail.minAmountPerUser);
      const maxUsdt = detail?.maxAmountPerUser != null ? Number(detail.maxAmountPerUser) : 10000;
      const { effectiveMin, maxForSliderCap, validationMaxCap } = getAicopyEffectiveMinMax({
        minUsdt,
        maxUsdt,
        currency,
        rate,
        convertConfig: convertCfg,
        currentAvailableNum,
      });
      if (!Number.isFinite(effectiveMin) || effectiveMin <= 0) return;

      const { minIn, maxInForValidation } = computeAicopyStrategyCardAmountBounds({
        effectiveMin,
        maxForSliderCap,
        validationMaxCap,
      });
      if (!Number.isFinite(minIn) || !Number.isFinite(maxInForValidation) || minIn > maxInForValidation + 1e-8) return;

      if (!isAicopyBalanceSufficientForDefaultAmount(currentAvailableNum, minIn)) {
        if (currencyChanged) {
          toSet.push({ sid, amount: "" });
        }
        return;
      }

      const defaultAmountNum = minIn;
      if (!Number.isFinite(defaultAmountNum) || defaultAmountNum < minIn - 1e-8 || defaultAmountNum > maxInForValidation + 1e-8) return;

      const amount = defaultAmountNum.toFixed(2);
      if (currencyChanged) {
        toSet.push({ sid, amount });
      } else {
        if (defaultAmountSetForStrategiesRef.current.has(sid)) return;
        defaultAmountSetForStrategiesRef.current.add(sid);
        toSet.push({ sid, amount });
      }
    });

    if (toSet.length === 0) return;
    setFormStateByStrategy((prev) => {
      const next = { ...prev };
      toSet.forEach(({ sid, amount }) => {
        next[sid] = { ...(next[sid] || {}), amount };
      });
      return next;
    });
  }, [
    strategies,
    strategyDetails,
    availableBalance,
    usdcAvailableBalance,
    selectedCurrencyByStrategy,
    fiatRatesMap,
    fiatBalanceMap,
    convertConfigList,
  ]);

  const endOrderFiatOptions = useMemo(
    () => getRecommendedFiatCodesFromList(fund?.recommendedCurrencyList || []).slice(0, 3),
    [fund?.recommendedCurrencyList]
  );

  /** 跟单成功后回填当前策略卡片的默认下单金额（最低入场资金） */
  const getDefaultFormAmountForStrategy = useCallback(
    (strategyId) => {
      const detail = strategyDetails[strategyId];
      if (detail?.minAmountPerUser == null) return "";
      const currency = selectedCurrencyByStrategy[strategyId] ?? "USDT";
      const minUsdt = Number(detail.minAmountPerUser);
      const maxUsdt = detail?.maxAmountPerUser != null ? Number(detail.maxAmountPerUser) : 10000;
      const isVoucherPayStrategy = isAicopyVoucherOrderOption(currency);
      const currentAvailableBalanceStr = resolveAicopyCurrentAvailableBalanceStr({
        currency,
        availableBalance,
        usdcAvailableBalance,
        fiatBalanceMap,
        isVoucherPay: isVoucherPayStrategy,
      });
      const currentAvailableNum = parseAicopyBalanceNum(currentAvailableBalanceStr);
      const convertCfg = findAicopyPayToUsdtConfig(convertConfigList, currency);
      const rate = resolveAicopyPayConvertRate({
        currency,
        fiatRate: fiatRatesMap[currency],
        convertConfig: convertCfg,
      });
      const { effectiveMin, maxForSliderCap, validationMaxCap } = getAicopyEffectiveMinMax({
        minUsdt,
        maxUsdt,
        currency,
        rate,
        convertConfig: convertCfg,
        currentAvailableNum,
      });
      if (!Number.isFinite(effectiveMin) || effectiveMin <= 0) return "";
      const { minIn, maxInForValidation } = computeAicopyStrategyCardAmountBounds({
        effectiveMin,
        maxForSliderCap,
        validationMaxCap,
      });
      if (!Number.isFinite(minIn) || !Number.isFinite(maxInForValidation) || minIn > maxInForValidation + 1e-8) return "";
      if (!isAicopyBalanceSufficientForDefaultAmount(currentAvailableNum, minIn)) return "";
      return minIn.toFixed(2);
    },
    [
      strategyDetails,
      selectedCurrencyByStrategy,
      fiatRatesMap,
      fiatBalanceMap,
      availableBalance,
      usdcAvailableBalance,
      convertConfigList,
    ]
  );

  const resetFormAmountToMinAfterFollow = useCallback(
    (strategyId) => {
      const amount = getDefaultFormAmountForStrategy(strategyId);
      setFormStateByStrategy((prev) => ({
        ...prev,
        [strategyId]: { ...(prev[strategyId] || { showDropdown: false }), amount },
      }));
      if (amount) defaultAmountSetForStrategiesRef.current.add(strategyId);
    },
    [getDefaultFormAmountForStrategy]
  );

  // 获取所有运行中的AI策略列表
  const loadStrategies = async (isLoadMore = false) => {
    if (strategiesLoadingRef.current) return;
    strategiesLoadingRef.current = true;
    setStrategiesLoading(true);
    try {
      const pageSize = 10;
      const nextOffset = isLoadMore ? Number(strategiesOffset) || 0 : 0;
      const params = {
        limit: pageSize,
        offset: nextOffset,
      };

      const response = await get_strategies({
        params,
        isResFull: true,
      });
      if (Array.isArray(response.result)) {
        const incoming = response.result || [];
        if (isLoadMore) {
          setStrategies((prev) => {
            const prevList = Array.isArray(prev) ? prev : [];
            const map = new Map();
            prevList.forEach((s) => s?.strategyId && map.set(s.strategyId, s));
            incoming.forEach((s) => s?.strategyId && map.set(s.strategyId, s));
            return Array.from(map.values());
          });
        } else {
          setStrategies(response.result);
        }

        // offset 语义为“数值偏移”，避免用 strategyId 当游标导致重复页
        if (incoming.length > 0) {
          setStrategiesOffset(nextOffset + incoming.length);
          setStrategiesHasMore(incoming.length >= pageSize);

          // 为每个策略加载近 90 日收益走势（与「过去90天收益率」文案一致）
          incoming.forEach((strategy) => {
            if (strategy.strategyId) {
              loadStrategyReturnTrend(strategy.strategyId);
            }
          });
        } else {
          setStrategiesHasMore(false);
        }
      } else {
        console.error("获取策略列表失败:", response.mc);
        if (!isLoadMore) {
          setStrategies([]);
        }
        setStrategiesHasMore(false);
      }
    } catch (error) {
      console.error("获取策略列表接口调用失败", error);
      if (!isLoadMore) {
        setStrategies([]);
      }
      setStrategiesHasMore(false);
    } finally {
      setStrategiesLoading(false);
      strategiesLoadingRef.current = false;
    }
  };
  loadStrategiesRef.current = loadStrategies;

  const MAX_VOUCHER_AUTO_FOLLOW_LOAD_MORE = 3;

  // 用户自行进入 /aicopy：有可用券则自动「用券下单」，并滚动到首个可下单策略
  useEffect(() => {
    if (!router.isReady) return;
    if (isAicopyVoucherUseEntryQuery(router.query)) return;
    if (!loginStatus) return;
    if (voucherAutoFollowAppliedRef.current) return;
    if (voucherAutoFollowInFlightRef.current) return;
    if (activeTab !== "aiStrategy") return;
    if (!strategies.length || strategiesLoading) return;

    const userId = userInfo?.userId;
    const tenantId = resolveCouponTenantId(appState);
    if (userId == null || userId === "" || tenantId == null) return;

    const hasAnyDetail = strategies.some(
      (s) => s?.strategyId != null && strategyDetails[s.strategyId]?.minAmountPerUser != null
    );
    if (!hasAnyDetail) return;

    voucherAutoFollowInFlightRef.current = true;
    let cancelled = false;

    (async () => {
      try {
        const { ok, items } = await fetchAicopyUsableVouchersCached({ tenantId, userId });
        if (cancelled) return;
        if (!ok || !items?.length) {
          voucherAutoFollowAppliedRef.current = true;
          return;
        }

        setHasUsableVouchers(true);
        setUsableVouchers(items);

        const target = findAicopyVoucherFollowTarget({
          strategies,
          strategyDetails,
          vouchers: items,
          preferredVoucherUserCouponId: null,
          availableBalance,
        });

        if (!target) {
          if (
            strategiesHasMore &&
            voucherAutoFollowLoadMoreAttemptsRef.current < MAX_VOUCHER_AUTO_FOLLOW_LOAD_MORE &&
            loadStrategiesRef.current
          ) {
            voucherAutoFollowLoadMoreAttemptsRef.current += 1;
            loadStrategiesRef.current(true);
            return;
          }
          voucherAutoFollowAppliedRef.current = true;
          if (items[0]?.id != null) {
            setPreferredVoucherUserCouponId(String(items[0].id));
          }
          return;
        }

        voucherAutoFollowAppliedRef.current = true;
        setPreferredVoucherUserCouponId(target.voucherUserCouponId);

        if (
          !userPayCurrencyTouchedByStrategyRef.current[target.strategyId] &&
          !isAicopyVoucherOrderOption(selectedCurrencyByStrategy[target.strategyId])
        ) {
          setSelectedCurrencyByStrategy((prev) => ({
            ...prev,
            [target.strategyId]: AICOPY_ORDER_OPTION_VOUCHER,
          }));
          defaultPayCurrencyAppliedByStrategyRef.current.add(target.strategyId);
        }

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (cancelled) return;
            scrollToAicopyStrategyCard(target.strategyId);
          });
        });
      } finally {
        if (!cancelled) {
          voucherAutoFollowInFlightRef.current = false;
        }
      }
    })();

    return () => {
      cancelled = true;
      voucherAutoFollowInFlightRef.current = false;
    };
  }, [
    router.isReady,
    router.query,
    router.asPath,
    loginStatus,
    activeTab,
    strategies,
    strategyDetails,
    strategiesLoading,
    strategiesHasMore,
    availableBalance,
    selectedCurrencyByStrategy,
    userInfo?.userId,
    appState,
  ]);

  // 获取单个策略的近 90 日收益走势（GET .../return-trend?days=90）
  const loadStrategyReturnTrend = async (strategyId) => {
    try {
      const response = await get_strategyReturnTrend(strategyId, 90, {
        isResFull: true,
      });

      if (response.rc === 0 && response.result) {
        // result 可能是数组或对象，需要处理
        let trendData = [];
        if (Array.isArray(response.result)) {
          trendData = response.result;
        } else if (response.result) {
          trendData = [response.result];
        }

        setStrategyReturnTrends((prev) => ({
          ...prev,
          [strategyId]: trendData,
        }));
      }
    } catch (error) {
      console.error(`获取策略 ${strategyId} 收益率走势图失败:`, error);
    }
  };

  const closeSettlementSheet = () => {
    setSettlementSheet({ open: false, mode: null, positionId: null, order: null });
  };

  const handleEndOrder = (order, e) => {
    e?.stopPropagation?.();
    const positionId = resolveAicopyPositionId(order);
    if (!positionId) {
      message.warning(t("aicopy.unfollowFailed"));
      return;
    }
    setSettlementSheet({
      open: true,
      mode: "unfollow",
      positionId,
      order: order || null,
    });
  };

  const handleAddPosition = (strategyId) => {
    if (!strategyId) return;
    router.push(`/aicopy/${strategyId}`);
  };

  const openWithdrawProfitModal = (order, e) => {
    e?.stopPropagation?.();
    const positionId = resolveAicopyPositionId(order);
    const gross = Number(order?.grossProfitRaw) || parseAicopyGrossProfitRaw(order);
    const withdrawnRaw = order?.withdrawnProfitAmount ?? order?.withdrawn_profit_amount ?? "0";
    const withdrawn = Math.max(0, parseFloat(String(withdrawnRaw).replace(/,/g, "")) || 0);
    const profitFullyWithdrawn = withdrawn + 1e-6 >= gross;
    if (gross <= 0) {
      message.warning(t("aicopy.withdrawProfitNoAvailable"));
      return;
    }
    if (profitFullyWithdrawn) {
      message.warning(t("aicopy.withdrawProfitFullyWithdrawn"));
      return;
    }
    if (!positionId) {
      message.warning(t("aicopy.withdrawProfitFailed"));
      return;
    }
    setSettlementSheet({
      open: true,
      mode: "withdraw",
      positionId,
      order: { ...order, grossProfitRaw: gross },
    });
  };

  const settlementSourceUsdtAmount = useMemo(() => {
    if (!settlementSheet.open) return "0";
    const order = settlementSheet.order;
    if (!order) return "0";
    if (settlementSheet.mode === "withdraw") {
      return computeWithdrawableProfitUsdtStr(order) || String(order.grossProfitRaw ?? "0");
    }
    if (settlementSheet.mode === "unfollow") {
      return computeEstimatedSettledUsdtStr(order);
    }
    return "0";
  }, [settlementSheet]);

  const handleSelectedVoucherChange = useCallback((strategyId, row) => {
    if (!strategyId) return;
    setSelectedVoucherByStrategy((prev) => {
      const next = { ...prev };
      if (!row?.id) {
        delete next[strategyId];
      } else {
        next[strategyId] = row;
      }
      selectedVoucherByStrategyRef.current = next;
      return next;
    });
  }, []);

  // 策略跟单 tab：立即跟单提交；用券走 follow-by-coupon；法币走兑换弹窗；USDT 走 follow
  const handleFollowSubmit = async (strategyId, amountStr, currency) => {
    if (!strategyId) return;

    if (isAicopyVoucherOrderOption(currency)) {
      const coupon =
        selectedVoucherByStrategyRef.current[strategyId] ?? selectedVoucherByStrategy[strategyId];
      const couponId = coupon?.id;
      if (couponId == null || couponId === "") {
        setFollowErrorDepositCurrency(currency);
        setFollowErrorModalMessage(t("aicopy.voucherStripEmpty") || "Please select a voucher");
        setFollowErrorIsInsufficientFunds(false);
        setShowFollowErrorModal(true);
        return;
      }
      setSubmittingStrategyId(strategyId);
      try {
        const response = await followByCoupon(couponId, strategyId, {
          isResFull: true,
          autoErrorModal: false,
        });
        if (response.rc === 0) {
          resetFormAmountToMinAfterFollow(strategyId);
          const displayAmount = coupon?.amount != null ? String(coupon.amount) : "";
          const displayUnit = coupon?.unit || "USDT";
          setFollowSuccessDisplay({ amount: displayAmount, currency: displayUnit });
          setShowFollowSuccessModal(true);
          selectedVoucherByStrategyRef.current = {};
          setSelectedVoucherByStrategy({});
          invalidateAicopyUsableVouchersCache();
          loadSummary();
          loadMyCurrent();
          loadBalance();
          loadFiatBalance();
        } else {
          setFollowErrorModalMessage(response.mc || "Follow failed");
          setFollowErrorIsInsufficientFunds(false);
          setShowFollowErrorModal(true);
        }
      } catch (error) {
        setFollowErrorModalMessage(error?.message || error?.mc || "Follow failed");
        setFollowErrorIsInsufficientFunds(false);
        setShowFollowErrorModal(true);
      } finally {
        setSubmittingStrategyId(null);
      }
      return;
    }

    const amount = parseFloat(String(amountStr).replace(/,/g, ""));
    if (!amount || amount <= 0) {
      setFollowErrorDepositCurrency(currency);
      setFollowErrorModalMessage(t("aicopy.enterInvestmentAmount") || "Please enter a valid amount");
      setFollowErrorIsInsufficientFunds(false);
      setShowFollowErrorModal(true);
      return;
    }

    if (currency && needsAicopyConvertBeforePay(currency)) {
      const convertCheck = validateAicopyPayToUsdtConvert(convertConfigList, currency, String(amount));
      if (!convertCheck.ok) {
        setFollowErrorModalMessage(t("convert.notAvailable") || "This currency is not supported for conversion, please use USDT");
        setFollowErrorIsInsufficientFunds(false);
        setShowFollowErrorModal(true);
        return;
      }
      pendingConvertRef.current = {
        mode: "follow",
        strategyId,
        fiatCode: currency,
        sourceAmount: String(amount),
      };
      setConvertConfirmDirection("fiatToUsdt");
      setConvertModalReceiveCurrency("USDT");
      setConvertModalSourceAmount(String(amount));
      setConvertPreviewData(null);
      setConvertPreviewLoading(true);
      setShowConvertConfirmModal(true);
      post_convertPreview(
        convertCheck.previewPayload,
        { isResFull: true, autoErrorModal: false }
      )
        .then((res) => {
          const result = res?.result ?? res?.data ?? res;
          const detail = strategyDetails[strategyId];
          const minUsdtForCheck = detail?.minAmountPerUser != null ? Number(detail.minAmountPerUser) : 200;
          if (result?.targetAmount != null) {
            if (!isAicopyFiatConvertTargetSufficient(result, minUsdtForCheck)) {
              setShowConvertConfirmModal(false);
              setFollowErrorModalMessage(
                t("aicopy.fiatConvertTargetBelowMin", {
                  receive: result.targetAmount,
                  min: minUsdtForCheck,
                }) || `Estimated USDT after fees (${result.targetAmount}) is below minimum (${minUsdtForCheck} USDT)`
              );
              setFollowErrorIsInsufficientFunds(false);
              setShowFollowErrorModal(true);
              return;
            }
            setConvertPreviewData(result || null);
          } else {
            setShowConvertConfirmModal(false);
            setFollowErrorModalMessage(t("convert.previewFailed") || "Preview failed, please try again");
            setFollowErrorIsInsufficientFunds(false);
            setShowFollowErrorModal(true);
          }
        })
        .catch((err) => {
          setConvertPreviewData(null);
          setShowConvertConfirmModal(false);
          setFollowErrorModalMessage(err?.mc || err?.message || t("convert.previewFailed") || "Preview failed");
          setFollowErrorIsInsufficientFunds(false);
          setShowFollowErrorModal(true);
        })
        .finally(() => setConvertPreviewLoading(false));
      return;
    }

    setSubmittingStrategyId(strategyId);
    try {
      const response = await follow(strategyId, amount, {
        isResFull: true,
        autoErrorModal: false,
      });
      if (response.rc === 0) {
        resetFormAmountToMinAfterFollow(strategyId);
        setFollowSuccessDisplay({ amount: amountStr, currency: "USDT" });
        setShowFollowSuccessModal(true);
        loadSummary();
        loadMyCurrent();
        loadBalance();
        loadFiatBalance();
      } else {
        setFollowErrorModalMessage(response.mc || "Follow failed");
        setFollowErrorIsInsufficientFunds(false);
        setShowFollowErrorModal(true);
      }
    } catch (error) {
      setFollowErrorModalMessage(error?.message || error?.mc || "Follow failed");
      setFollowErrorIsInsufficientFunds(false);
      setShowFollowErrorModal(true);
    } finally {
      setSubmittingStrategyId(null);
    }
  };

  // 兑换确认弹窗内点击确认：先 POST /convert/order，成功后再 follow
  const handleConvertConfirmSubmit = useCallback(async () => {
    const pending = pendingConvertRef.current;

    if (!pending?.fiatCode || !pending?.sourceAmount || !pending?.strategyId) return;
    if (submittingStrategyId) return;

    setSubmittingStrategyId(pending.strategyId);
    setShowConvertConfirmModal(false);

    try {
      const orderPayload = buildAicopyPayToUsdtOrder(pending.fiatCode, pending.sourceAmount, convertConfigList);
      const convertRes = await executeAicopyConvertToUsdtOrder(orderPayload, {
        isResFull: true,
        autoErrorModal: false,
      }).catch((err) => {
        setFollowErrorModalMessage(err?.mc || err?.message || "Fiat conversion failed");
        setFollowErrorIsInsufficientFunds(false);
        setShowFollowErrorModal(true);
        return null;
      });

      if (convertRes == null) {
        setSubmittingStrategyId(null);
        return;
      }

      const convertResult = convertRes?.result ?? convertRes?.data ?? convertRes;
      const state = (convertResult?.state ?? "").toUpperCase();
      const targetAmount = convertResult?.targetAmount != null ? String(convertResult.targetAmount) : null;

      if (state !== "SUCCEED" || !targetAmount) {
        setFollowErrorModalMessage(t("convert.orderProcessing") || "Conversion in progress, please try again later on the follow page");
        setFollowErrorIsInsufficientFunds(false);
        setShowFollowErrorModal(true);
        setSubmittingStrategyId(null);
        return;
      }

      loadBalance();
      loadFiatBalance();

      const amountUsdt = parseFloat(targetAmount) || 0;
      const response = await follow(pending.strategyId, amountUsdt, {
        isResFull: true,
        autoErrorModal: false,
      });

      if (response.rc === 0) {
        resetFormAmountToMinAfterFollow(pending.strategyId);
        setFollowSuccessDisplay({ amount: formatApiAmount(targetAmount, "USDT"), currency: "USDT" });
        setShowFollowSuccessModal(true);
        loadSummary();
        loadMyCurrent();
        loadBalance();
      } else {
        setFollowErrorModalMessage(response.mc || "Follow failed");
        setFollowErrorIsInsufficientFunds(false);
        setShowFollowErrorModal(true);
      }
    } catch (error) {
      setFollowErrorModalMessage(error?.message || error?.mc || "Follow failed");
      setFollowErrorIsInsufficientFunds(false);
      setShowFollowErrorModal(true);
    } finally {
      setSubmittingStrategyId(null);
    }
  }, [submittingStrategyId, t, loadFiatBalance, loadBalance, convertConfigList]);

  const handleItemClick = (itemId, type) => {
    // type: 'order' 或 'strategy'
    router.push(`/aicopy/${itemId}`);
  };

  const renderLiveTickerSegments = (loopKey) => {
    const rows = liveTickers.length ? liveTickers : [];
    return rows.map((item, idx) => {
      const pairText = formatTickerPairLabel(item.s);
      const dir = getLiveTickerReturnDirection(item.pnlRateText);
      const pillClass =
        dir === "up"
          ? `${styles.liveTickerChangePill} ${styles.liveTickerChangePillUp}`
          : dir === "down"
          ? `${styles.liveTickerChangePill} ${styles.liveTickerChangePillDown}`
          : `${styles.liveTickerChangePill} ${styles.liveTickerChangePillFlat}`;
      return (
        <span key={`${loopKey}-${item.positionKey ?? item.s}-${idx}`} className={styles.liveTickerItem}>
          <span className={styles.liveTickerPair}>{pairText}</span>
          <span className={styles.liveTickerPair}> {item.directionLabel}</span>
          <span className={styles.liveTickerPair}> PNL </span>
          <span className={styles.liveTickerPrice}>{item.pnlAmountText}</span>
          <span className={pillClass}>
            {dir === "up" ? <img src={liveTickerTrendUp} alt="" className={styles.liveTickerArrow} width={8} height={8} /> : null}
            {dir === "down" ? <img src={liveTickerTrendDown} alt="" className={styles.liveTickerArrow} width={8} height={8} /> : null}
            <span className={styles.liveTickerChangeText}>{item.pnlRateText}</span>
          </span>
        </span>
      );
    });
  };

  return (
    <div className={styles.aiCopy}>
      <div className={styles.stickyHeader}>
        <div className={styles.liveTickerBar} aria-label={t("aicopy.liveTickerAria")}>
          <div className={styles.liveLead}>
            <div className={styles.liveBadge}>
              <span className={styles.liveBadgeDot} aria-hidden />
              <span>{t("aicopy.live")}</span>
            </div>
          </div>
          <div className={styles.liveMarquee}>
            <div className={styles.liveMarqueeTrack} key={liveTickers.length > 0 ? "live-marquee-on" : "live-marquee-off"}>
              <div className={styles.liveMarqueeLoop}>{renderLiveTickerSegments("a")}</div>
              <div className={styles.liveMarqueeLoop} aria-hidden="true">
                {renderLiveTickerSegments("b")}
              </div>
            </div>
          </div>
        </div>
        {/* <div className={styles.navbar}>
          <div className={styles.navTitle}>{t("aicopy.title")}</div>
        </div> */}
      </div>

      {/* 跟单资产 Figma 14055:27939 */}
      <div className={styles.assetSection}>
        <div className={styles.assetCard}>
          <div
            className={`${styles.assetCardInner} ${
              loginStatus && assetChartExpanded ? styles.assetCardInnerWithChart : ""
            }`}
          >
            <div
              className={`${styles.assetHeader} ${
                loginStatus && assetChartExpanded ? styles.assetHeaderWithChart : ""
              }`}
            >
              <div className={styles.assetHeaderTop}>
                <div className={styles.assetTitleWrapper}>
                  <span className={styles.assetTitle}>{t("aicopy.totalAssets")}</span>
                  <img src={eyeIcon} alt="toggle visibility" className={styles.eyeIcon} onClick={() => setIsAssetVisible(!isAssetVisible)} />
                </div>
                <button
                  type="button"
                  className={styles.assetInfoBtn}
                  aria-label={t("aicopy.assetRulesTitle")}
                  onClick={() => setShowAssetRulesModal(true)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden>
                    <path
                      d="M7.3501 7.3501V3.9751M7.3501 9.85168V9.88135M14.1001 7.3501C14.1001 11.078 11.078 14.1001 7.3501 14.1001C3.62218 14.1001 0.600098 11.078 0.600098 7.3501C0.600098 3.62218 3.62218 0.600098 7.3501 0.600098C11.078 0.600098 14.1001 3.62218 14.1001 7.3501Z"
                      stroke="white"
                      strokeOpacity="0.56"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
              <div className={styles.assetValue}>
                {isAssetVisible ? (
                  <>
                    <span className={styles.assetAmount}>{assetData.totalAssets}</span>
                    <div className={styles.assetUnitRow}>
                      <span className={styles.assetUnit}>USDT</span>
                      {loginStatus ? (
                        <button
                          type="button"
                          className={`${styles.assetChartToggle} ${assetChartExpanded ? styles.assetChartToggleExpanded : ""}`}
                          aria-label={assetChartExpanded ? "Collapse chart" : "Expand chart"}
                          aria-expanded={assetChartExpanded}
                          onClick={() => setAssetChartExpanded((v) => !v)}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="8" height="4" viewBox="0 0 8 4" fill="none" aria-hidden>
                            <path d="M8 0H0L4 4L8 0Z" fill="white" fillOpacity="0.56" />
                          </svg>
                        </button>
                      ) : null}
                    </div>
                  </>
                ) : (
                  <span className={styles.assetAmount}>****</span>
                )}
              </div>
              {loginStatus && assetChartExpanded ? (
                <AicopyAssetCurveChart enabled={isAssetVisible} />
              ) : null}
            </div>
            <div className={styles.assetAccentLine} aria-hidden />
            <div className={styles.assetStats}>
              <div className={styles.statItem}>
                <div className={styles.statLabel}>{t("aicopy.cumulativeReturnRate")}</div>
                <div className={`${styles.statValue} ${isAssetVisible ? getProfitColorClass(assetData.todayProfit, styles) : ""}`}>
                  {isAssetVisible ? assetData.todayProfit : "****"}
                </div>
              </div>
              <div className={styles.statItem}>
                <div className={styles.statLabel}>{t("aicopy.winRate")}</div>
                <div className={`${styles.statValue} ${isAssetVisible ? getProfitColorClass(assetData.yesterdayProfit, styles) : ""}`}>
                  {isAssetVisible ? assetData.yesterdayProfit : "****"}
                </div>
              </div>
              <div className={`${styles.statItem} ${styles.statItemAlignEnd}`}>
                <div className={styles.statLabel}>{t("aicopy.totalProfit")}</div>
                <div className={`${styles.statValue} ${isAssetVisible ? getProfitColorClass(assetData.totalProfit, styles) : ""}`}>
                  {isAssetVisible ? assetData.totalProfit : "****"}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.assetQuickStrip}>
          <div className={styles.assetQuickFollowRow}>
            <div className={styles.assetQuickFollowMain}>
              <img
                src={topStrategyByReturn?.avatarUrl || strategyIconAlpha10}
                alt=""
                className={styles.assetQuickStrategyAvatar}
              />
              <span className={styles.assetQuickFollowName}>
                {strategiesLoading && strategies.length === 0 ? "…" : (topStrategyByReturn?.strategyName || "").trim() || "—"}
              </span>
              <span
                className={`${styles.assetQuickFollowRate} ${
                  topStrategyByReturn
                    ? getProfitColorClass(topStrategyByReturn.return90d ?? topStrategyByReturn.return_90d ?? topStrategyByReturn.totalReturnRate, styles) ||
                      styles.assetQuickFollowRateMuted
                    : styles.assetQuickFollowRateMuted
                }`}
              >
                {strategiesLoading && strategies.length === 0
                  ? "…"
                  : topStrategyByReturn
                  ? (() => {
                      const raw = topStrategyByReturn.return90d ?? topStrategyByReturn.return_90d ?? topStrategyByReturn.totalReturnRate;
                      const n = parseFloat(raw);
                      if (Number.isNaN(n)) return "--";
                      const s = String(raw).replace(/,/g, "");
                      return n > 0 ? `+${s}%` : n < 0 ? `${s}%` : "0%";
                    })()
                  : "--"}
              </span>
            </div>
            <button
              type="button"
              className={styles.assetQuickFollowAction}
              onClick={() => {
                const id = topStrategyByReturn?.strategyId;
                if (id) router.push(`/aicopy/${id}`);
              }}
            >
              {t("aicopy.oneClickFollow")}
            </button>
          </div>
        </div>
      </div>

      {/* 标签切换：Strategy Copy Trading 在左，My Copy Trading 在右 */}
      <div className={styles.tabs}>
        <button
          type="button"
          className={`${styles.tab} ${activeTab === "aiStrategy" ? styles.active : ""}`}
          onClick={() => {
            setActiveTab("aiStrategy");
            setStrategiesOffset(0);
            setStrategiesHasMore(true);
            loadStrategies(false);
          }}
        >
          {t("aicopy.strategyOrders")}
        </button>
        <button type="button" className={`${styles.tab} ${activeTab === "aiLab" ? styles.active : ""}`} onClick={() => setActiveTab("aiLab")}>
          {t("aicopy.aiLab")}
        </button>
        <button
          type="button"
          className={`${styles.tab} ${styles.tabWithOrderCount} ${activeTab === "myOrders" ? styles.active : ""}`}
          onClick={() => {
            setActiveTab("myOrders");
            loadMyCurrent();
          }}
        >
          <span className={styles.tabOrderLabel}>
            {t("aicopy.myOrders")}
            <span className={styles.orderCountBadgeAnchor} aria-hidden>
              <span className={styles.orderCountTag}>{orders.length || 0}</span>
              <svg
                className={styles.orderCountArrow}
                xmlns="http://www.w3.org/2000/svg"
                width="10"
                height="5"
                viewBox="0 0 4 2"
                fill="none"
              >
                <path
                  d="M4 0H0L1.29289 1.29289C1.68342 1.68342 2.31658 1.68342 2.70711 1.29289L4 0Z"
                  fill={`url(#${orderCountArrowGradId})`}
                />
                <defs>
                  <linearGradient
                    id={orderCountArrowGradId}
                    x1="0.473684"
                    y1="0.4375"
                    x2="3.88745"
                    y2="1.06635"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#B9F78A" />
                    <stop offset="1" stopColor="#64E800" />
                  </linearGradient>
                </defs>
              </svg>
            </span>
          </span>
        </button>
        <button
          type="button"
          className={`${styles.tab} ${activeTab === "orderHistory" ? styles.active : ""}`}
          onClick={() => setActiveTab("orderHistory")}
        >
          {t("aicopy.orderHistory")}
        </button>
      </div>

      {/* 我的跟单列表 */}
      {activeTab === "myOrders" && (
        <div className={styles.ordersList}>
          {ordersLoading ? (
            <div style={{ padding: "20px", textAlign: "center", color: "rgba(255, 255, 255, 0.56)" }}>{t("aicopy.loading")}</div>
          ) : orders.length === 0 ? (
            <div style={{ padding: "20px", textAlign: "center", color: "rgba(255, 255, 255, 0.56)" }}>{t("aicopy.noData")}</div>
          ) : (
            orders.map((order) => {
              const couponExpireMs = isAicopyCouponPosition(order) ? parseCouponExpireTimeMs(order) : null;
              const unwithdrawnProfitStr = computeWithdrawableProfitUsdtStr(order);

              return (
                <div key={resolveAicopyPositionId(order) ?? order.strategyId} className={styles.orderCard} onClick={() => handleItemClick(order.strategyId, "order")}>
                  <OrderCardCornerBadge raw={order} t={t} />
                  <div className={styles.orderHeaderBlock}>
                  <div className={styles.orderHeader}>
                    <div className={styles.orderInfo}>
                      <div className={styles.orderIconWrapper}>
                        <img src={order.avatarUrl || strategyIconAlpha10} alt={order.strategyName} className={styles.orderIcon} />
                      </div>
                      <div className={styles.orderNameWrapper}>
                        <div className={styles.orderNameGroup}>
                          <span className={styles.orderName}>{order.strategyName}</span>
                          <div className={styles.arrowIcon}>
                            <img src={arrowRight} alt="arrow" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={styles.orderAiStatusRow}>
                    <AiWorkingStatusBadge
                      label={getQuantStrategyStatusLabel(order.strategyId, t("aicopy.aiWorkingStatus"))}
                    />
                  </div>
                  </div>

                  <div className={styles.orderStats}>
                    <div className={styles.orderStatRow}>
                      <div className={styles.orderStatItem}>
                        <div className={styles.orderStatLabel}>{t("aicopy.withdrawnProfitAmount")}</div>
                        <div
                          className={`${styles.orderStatValue} ${getProfitColorClass(unwithdrawnProfitStr, styles)}`}
                        >
                          {formatNumber(unwithdrawnProfitStr)}
                        </div>
                      </div>
                      <div className={`${styles.orderStatItem} ${styles.orderStatItemEnd}`}>
                        <div className={styles.orderStatLabel}>{t("aicopy.totalInvestment")}</div>
                        <div className={styles.orderStatValue}>{order.totalInvestAmount || "0.00"}</div>
                      </div>
                    </div>
                    <div className={styles.orderStatRow}>
                      <div className={styles.orderStatItem}>
                        <div className={styles.orderStatLabel}>{t("aicopy.returnRate")}</div>
                        <div className={`${styles.orderStatValue} ${getProfitColorClass(order.totalReturnRate, styles)}`}>
                          {order.totalReturnRate ?? "0.00%"}
                        </div>
                      </div>
                      <div className={`${styles.orderStatItem} ${styles.orderStatItemEnd}`}>
                        <div className={styles.orderStatLabel}>{t("aicopy.realizedProfit")}</div>
                        <div className={`${styles.orderStatValue} ${getProfitColorClass(order.profit, styles)}`}>{order.profit ?? ""}</div>
                      </div>
                    </div>
                  </div>

                  <AicopyOrderFollowMetaRows
                    startTime={order.startTime}
                    expireMs={couponExpireMs}
                    t={t}
                    detailClassName={styles.orderDetails}
                  />

                  {isAicopyCouponPosition(order) ? (
                    <CouponPositionActionBar onWithdraw={(e) => openWithdrawProfitModal(order, e)} t={t} />
                  ) : (
                    <div className={styles.orderActionsCol} onClick={(e) => e.stopPropagation()}>
                      <div className={styles.orderActionsRow}>
                        <button type="button" className={styles.withdrawProfitBtn} onClick={(e) => openWithdrawProfitModal(order, e)}>
                          {t("aicopy.withdrawProfit")}
                        </button>
                        <button type="button" className={styles.addPositionBtn} onClick={(e) => { e.stopPropagation(); handleAddPosition(order.strategyId); }}>
                          {t("aicopy.addPosition")}
                        </button>
                      </div>
                      <button type="button" className={styles.orderEndFollowLink} onClick={(e) => handleEndOrder(order, e)}>
                        {t("aicopy.endOrder")}
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      <OrderHistoryTab active={activeTab === "orderHistory"} t={t} onCardClick={handleItemClick} />

      {activeTab === "aiLab" && (
        <div className={styles.aiLabSection}>
          <AiLabFearGreedCard data={aiLabFearGreed} loading={aiLabFearGreedLoading} />
          <AiLabJointLabCard
            items={aiLabHistory}
            loading={aiLabQuantMessagesLoading}
            strategyOptions={aiLabQuantStrategyOptions}
            activeStrategyId={aiLabQuantStrategyId}
            onStrategyChange={setAiLabQuantStrategyId}
            onCopyTrade={() => router.push("/aicopy/my-orders")}
          />
          <AiLabPnlRankingCard items={aiLabPnlRanking} loading={aiLabPnlRankingLoading} activeAiType={aiLabPnlAiType} onAiTypeChange={setAiLabPnlAiType} />
        </div>
      )}

      {/* 策略跟单列表 */}
      {activeTab === "aiStrategy" && (
        <div className={styles.strategiesList}>
          {strategies.map((strategy) => {
            const detail = strategyDetails[strategy.strategyId];
            const minUsdt = detail?.minAmountPerUser != null ? Number(detail.minAmountPerUser) : 200;
            const maxUsdt = detail?.maxAmountPerUser != null ? Number(detail.maxAmountPerUser) : 10000;
            const formState = formStateByStrategy[strategy.strategyId] || { amount: "", showDropdown: false };
            const selectedCurrency = selectedCurrencyByStrategy[strategy.strategyId] ?? "USDT";
            const strategyConvertConfig = findAicopyPayToUsdtConfig(convertConfigList, selectedCurrency);
            const isVoucherPayStrategy = isAicopyVoucherOrderOption(selectedCurrency);
            const currentAvailableBalanceStr = resolveAicopyCurrentAvailableBalanceStr({
              currency: selectedCurrency,
              availableBalance,
              usdcAvailableBalance,
              fiatBalanceMap,
              isVoucherPay: isVoucherPayStrategy,
            });
            const currentAvailableNum = parseAicopyBalanceNum(currentAvailableBalanceStr);
            const rate = resolveAicopyPayConvertRate({
              currency: selectedCurrency,
              fiatRate: fiatRatesMap[selectedCurrency],
              convertConfig: strategyConvertConfig,
            });
            const { effectiveMin, maxForSliderCap, validationMaxCap } = getAicopyEffectiveMinMax({
              minUsdt,
              maxUsdt,
              currency: selectedCurrency,
              rate,
              convertConfig: strategyConvertConfig,
              currentAvailableNum,
            });
            const {
              minIn: effectiveMinForInput,
              maxInForValidation: effectiveMaxForInput,
              maxInCurrentForSlider,
            } = computeAicopyStrategyCardAmountBounds({ effectiveMin, maxForSliderCap, validationMaxCap });
            const amountNum = parseFloat(String(formState.amount).replace(/,/g, ""));
            const amountRangeErrorMsg =
              isVoucherPayStrategy
                ? null
                : formState.amount && amountNum > 0
                  ? getAicopyInvestmentAmountErrorMsg({
                      amountNum,
                      currentAvailableNum,
                      currentAvailableBalanceStr,
                      minIn: effectiveMinForInput,
                      maxIn: effectiveMaxForInput,
                      formatNumber,
                      currency: selectedCurrency,
                      t,
                    })
                  : null;
            const currencyOptions = getAicopyStrategyOrderCurrencyOptions(
              fiatOptions,
              resolveAicopyStrategyPayCurrencySortCtx(
                strategyPayCurrencySortCtx,
                usableVouchers,
                detail,
                availableBalance
              )
            );
            const availableBalanceText = isVoucherPayStrategy
              ? `${formatAicopyAvailableBalanceDisplay(availableBalance, formatNumber)} USDT`
              : isAicopyUsdt(selectedCurrency)
                ? `${formatAicopyAvailableBalanceDisplay(availableBalance, formatNumber)} USDT`
                : isAicopyUsdc(selectedCurrency)
                  ? `${formatAicopyAvailableBalanceDisplay(usdcAvailableBalance, formatNumber)} USDC`
                  : `${formatAicopyAvailableBalanceDisplay(currentAvailableBalanceStr, formatNumber)} ${selectedCurrency}`;
            const avgDailyReturnRate = getAvgDailyReturnRateFromStrategyDetail(detail);
            const estimatedDailyProfitText = buildAicopyEstimatedDailyProfitText({
              amountNum: Number.isFinite(amountNum) ? amountNum : null,
              avgDailyReturnRate,
              formatNumber,
              currency: isVoucherPayStrategy ? "USDT" : selectedCurrency,
              t,
            });
            const return90dDisplay = formatStrategyReturn90dPercent(strategy);
            return (
              <div
                key={strategy.strategyId}
                className={styles.strategyCard}
                data-aicopy-strategy-id={strategy.strategyId}
              >
                <div className={styles.strategyHeader} onClick={() => handleItemClick(strategy.strategyId, "strategy")}>
                  <div className={styles.strategyInfo}>
                    <div className={styles.strategyIconWrapper}>
                      <img src={strategy.avatarUrl || strategyIconAlpha10} alt={strategy.strategyName} className={styles.strategyIcon} />
                    </div>
                    <div className={styles.strategyNameWrapper}>
                      <div className={styles.strategyNameGroup}>
                        <span className={styles.strategyName}>{strategy.strategyName}</span>
                        <div className={styles.arrowIcon}>
                          <img src={arrowRight} alt="arrow" />
                        </div>
                      </div>
                      <div className={styles.strategyTag}>
                        {strategy.tags && strategy.tags.length > 0
                          ? strategy.tags.map((tag, index) => (
                              <div key={index} className={styles.tagItem}>
                                <div className={styles.tagBg}>
                                  <img src={tagBackground} alt="tag bg" className={styles.tagBgImg} />
                                </div>
                                <div className={styles.tagIcon}>
                                  <img src={tagIcon} alt="tag icon" />
                                </div>
                                <span className={styles.tagText}>{tag}</span>
                              </div>
                            ))
                          : null}
                      </div>
                    </div>
                  </div>
                </div>

                <div className={styles.strategyAiStatusRow}>
                  <AiWorkingStatusBadge
                    label={getQuantStrategyStatusLabel(strategy.strategyId, t("aicopy.aiWorkingStatus"))}
                  />
                </div>

                <div className={styles.strategyStats}>
                  <div className={styles.strategyStatRow}>
                    <div className={styles.strategyReturnRate}>
                      <div className={`${styles.returnRateValue} ${getProfitColorClass(return90dDisplay.colorValue, styles)}`}>{return90dDisplay.text}</div>
                      <div className={styles.returnRateLabel}>{t("aicopy.returnRatePast90Days")}</div>
                    </div>
                    <div className={styles.strategyChart}>
                      {strategyReturnTrends[strategy.strategyId] && strategyReturnTrends[strategy.strategyId].length > 0 ? (
                        <ReturnRateChart data={strategyReturnTrends[strategy.strategyId]} />
                      ) : (
                        <></>
                      )}
                    </div>
                  </div>
                  <div className={styles.strategyStatRow}>
                    <div className={styles.strategyStatItem}>
                      <div className={styles.strategyStatValue}>{strategy.aum || "0"}</div>
                      <div className={styles.strategyStatLabel}>{t("aicopy.assetsUnderManagement")}</div>
                    </div>
                    <div className={styles.strategyStatItem}>
                      <div className={styles.strategyStatValue}>{strategy.followerCount || "0"}</div>
                      <div className={styles.strategyStatLabel}>{t("aicopy.userCount")}</div>
                    </div>
                    <div className={styles.strategyStatItem}>
                      <div className={`${styles.strategyStatValue} ${getProfitColorClass(strategy.totalProfit, styles)}`}>{strategy.totalProfit ?? "0.00"}</div>
                      <div className={styles.strategyStatLabel}>{t("aicopy.strategyPnl")}</div>
                    </div>
                  </div>
                </div>
                {strategy.description && (
                  <div className={styles.strategyDescWrap}>
                    <div className={expandedDescId === strategy.strategyId ? styles.strategyDescriptionExpanded : styles.strategyDescription}>
                      {strategy.description}
                    </div>
                    <span
                      className={styles.descToggle}
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedDescId(expandedDescId === strategy.strategyId ? null : strategy.strategyId);
                      }}
                    >
                      {expandedDescId === strategy.strategyId ? t("console.put.away") : t("console.expand")}
                    </span>
                  </div>
                )}

                <div className={styles.strategyCardDivider} />
                <div onClick={(e) => e.stopPropagation()} className={styles.strategyCardFormWrap}>
                  <OrderForm
                    value={formState.amount}
                    onChange={(v) =>
                      setFormStateByStrategy((prev) => ({
                        ...prev,
                        [strategy.strategyId]: { ...prev[strategy.strategyId], amount: v },
                      }))
                    }
                    onClear={() =>
                      setFormStateByStrategy((prev) => ({
                        ...prev,
                        [strategy.strategyId]: { ...prev[strategy.strategyId], amount: "" },
                      }))
                    }
                    currencyOptions={currencyOptions}
                    selectedCurrency={selectedCurrency}
                    onCurrencyChange={(newCurrency) => {
                      userPayCurrencyTouchedByStrategyRef.current[strategy.strategyId] = true;
                      setSelectedCurrencyByStrategy((prev) => ({
                        ...prev,
                        [strategy.strategyId]: newCurrency,
                      }));
                      setFormStateByStrategy((prev) => ({
                        ...prev,
                        [strategy.strategyId]: { ...prev[strategy.strategyId], amount: "" },
                      }));
                    }}
                    showCurrencyDropdown={formState.showDropdown}
                    onCurrencyDropdownToggle={() =>
                      setFormStateByStrategy((prev) => ({
                        ...prev,
                        [strategy.strategyId]: { ...prev[strategy.strategyId], showDropdown: !prev[strategy.strategyId]?.showDropdown },
                      }))
                    }
                    onCloseDropdown={() =>
                      setFormStateByStrategy((prev) => ({
                        ...prev,
                        [strategy.strategyId]: { ...prev[strategy.strategyId], showDropdown: false },
                      }))
                    }
                    minInCurrent={effectiveMinForInput}
                    maxInCurrent={effectiveMaxForInput}
                    formatNumber={formatNumber}
                    availableBalanceText={availableBalanceText}
                    amountRangeErrorMsg={amountRangeErrorMsg}
                    currentAvailableNum={currentAvailableNum}
                    currentAvailableBalanceStr={currentAvailableBalanceStr}
                    maxInCurrentForSlider={maxInCurrentForSlider}
                    onSliderChange={(val) => {
                      const balanceZeroOrBelowMin =
                        currentAvailableNum === 0 || (Number.isFinite(effectiveMinForInput) && currentAvailableNum < effectiveMinForInput);
                      if (balanceZeroOrBelowMin) {
                        setFollowErrorDepositCurrency(selectedCurrency);
                        setFollowErrorModalMessage(t("aicopy.insufficientBalanceGoDeposit"));
                        setFollowErrorIsInsufficientFunds(true);
                        setShowFollowErrorModal(true);
                        return;
                      }
                      setFormStateByStrategy((prev) => ({
                        ...prev,
                        [strategy.strategyId]: { ...prev[strategy.strategyId], amount: val },
                      }));
                    }}
                    onUseAll={() => {
                      if (currentAvailableNum > 0 && Number.isFinite(currentAvailableNum)) {
                        setFormStateByStrategy((prev) => ({
                          ...prev,
                          [strategy.strategyId]: { ...prev[strategy.strategyId], amount: currentAvailableBalanceStr },
                        }));
                      }
                    }}
                    onGoDeposit={(e) => {
                      e?.stopPropagation?.();
                      window.location.href = getUrl("/wallet/account/common/deposit");
                    }}
                    onGoFiatDeposit={(e) => {
                      e?.stopPropagation?.();
                      window.location.href = getUrl("/wallet/account/common/deposit/fiat");
                    }}
                    onGoVoucher={(e) => {
                      e?.stopPropagation?.();
                      window.location.href = getUrl("/activity/voucher");
                    }}
                    preferredVoucherUserCouponId={preferredVoucherUserCouponId}
                    onSelectedVoucherChange={(row) => handleSelectedVoucherChange(strategy.strategyId, row)}
                    submitLabel={t("aicopy.followNow")}
                    onSubmit={() => {
                      if (amountRangeErrorMsg) {
                        setFollowErrorDepositCurrency(selectedCurrency);
                        setFollowErrorModalMessage(amountRangeErrorMsg);
                        setFollowErrorIsInsufficientFunds(amountNum > currentAvailableNum + 1e-8);
                        setShowFollowErrorModal(true);
                        return;
                      }
                      handleFollowSubmit(strategy.strategyId, formState.amount, selectedCurrency);
                    }}
                    submitting={submittingStrategyId === strategy.strategyId}
                    submitDisabled={
                      isVoucherPayStrategy
                        ? !selectedVoucherByStrategy[strategy.strategyId]?.id ||
                          submittingStrategyId === strategy.strategyId
                        : !formState.amount || amountNum <= 0 || !!amountRangeErrorMsg || submittingStrategyId === strategy.strategyId
                    }
                    estimatedDailyProfitText={estimatedDailyProfitText}
                  />
                </div>
              </div>
            );
          })}
          {strategiesLoading && <div style={{ textAlign: "center", padding: "20px", color: "rgba(255, 255, 255, 0.56)" }}>{t("aicopy.loading")}</div>}
          {!strategiesLoading && strategies.length === 0 && (
            <div style={{ padding: "20px", textAlign: "center", color: "rgba(255, 255, 255, 0.56)" }}>{t("aicopy.noData")}</div>
          )}
        </div>
      )}

      <AicopySettlementSheet
        open={settlementSheet.open}
        mode={settlementSheet.mode}
        positionId={settlementSheet.positionId}
        sourceUsdtAmount={settlementSourceUsdtAmount}
        convertConfigList={convertConfigList}
        fiatOptionCodes={endOrderFiatOptions}
        onClose={closeSettlementSheet}
        onSuccess={() => {
          loadMyCurrent();
          loadSummary();
          loadBalance();
          loadFiatBalance();
        }}
      />


      {/* 立即跟单成功提示弹窗（与加仓页一致） */}
      <Modal
        open={showFollowSuccessModal}
        onCancel={() => {
          setShowFollowSuccessModal(false);
          setFollowSuccessDisplay(null);
        }}
        footer={null}
        closable={false}
        maskClosable={false}
        centered={true}
        width={343}
        zIndex={10051}
        className="successModal"
        maskStyle={{ background: "rgba(0, 0, 0, 0.4)" }}
      >
        <div className="modalContent">
          <div className="modalIcon">
            <img src={successIcon} alt="success" />
          </div>
          <div className="modalText">
            <div className="modalTitle">
              {t("aicopy.investmentSuccess", {
                amount: followSuccessDisplay?.amount ?? "",
                currency: followSuccessDisplay?.currency ?? "USDT",
              })}
            </div>
          </div>
          <FollowSuccessModalActions
            t={t}
            onOk={() => {
              setShowFollowSuccessModal(false);
              setFollowSuccessDisplay(null);
            }}
            onMyOrders={() => {
              setShowFollowSuccessModal(false);
              setFollowSuccessDisplay(null);
              setActiveTab("myOrders");
              loadMyCurrent();
            }}
          />
        </div>
      </Modal>

      <Modal
        open={showAssetRulesModal}
        onCancel={() => setShowAssetRulesModal(false)}
        footer={null}
        closable={false}
        maskClosable
        centered
        width={343}
        wrapClassName={styles.assetRulesModalWrap}
        bodyStyle={{ padding: 0 }}
        maskStyle={{ background: "rgba(0, 0, 0, 0.4)" }}
      >
        <div className={styles.assetRulesDialogPanel}>
          <div className={styles.assetRulesDialogTop}>
            <div className={styles.assetRulesDialogIconRow} aria-hidden>
              <svg className={styles.assetRulesDialogIcon} xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64" fill="none">
                <path
                  opacity="0.2"
                  d="M53.7793 10.2207C46.4896 2.931 34.6707 2.931 27.3811 10.2207L16.0671 21.5346H4.75342L42.4653 59.2465V47.9328L53.7793 36.6189C61.0689 29.3292 61.0689 17.5103 53.7793 10.2207Z"
                  fill="#AEB4BC"
                />
                <path
                  opacity="0.5"
                  d="M16.0668 47.9331C20.2324 52.0987 26.9862 52.0987 31.1518 47.9331L16.0668 32.8481C11.9012 37.0137 11.9012 43.7675 16.0668 47.9331Z"
                  fill="#AEB4BC"
                />
                <path
                  opacity="0.5"
                  d="M58.494 7.39148L56.6084 5.50586L52.8032 9.31105C53.1366 9.60049 53.4622 9.90371 53.7792 10.2207C54.0962 10.5377 54.3994 10.8633 54.6888 11.1967L58.494 7.39148Z"
                  fill="#AEB4BC"
                />
                <path d="M19.8384 17.7632L46.2366 44.1614L42.4653 47.9326L16.0671 21.5344L19.8384 17.7632Z" fill="#B9F78A" />
              </svg>
            </div>
            <h2 className={styles.assetRulesDialogTitle}>{t("aicopy.assetRulesTitle")}</h2>
            <div className={styles.assetRulesDialogDesc}>
              {parseAssetRulesSections(t("aicopy.assetRulesContent")).map((section, idx) => (
                <div key={idx} className={styles.assetRulesSection}>
                  <p className={styles.assetRulesSectionTitle}>{section.title}</p>
                  {section.bodyLines.length > 0 ? (
                    <div className={styles.assetRulesSectionBody}>
                      {section.bodyLines.map((line, lineIdx) => (
                        <p key={lineIdx}>{line}</p>
                      ))}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
          <button type="button" className={styles.assetRulesDialogBtn} onClick={() => setShowAssetRulesModal(false)}>
            {t("aicopy.ok")}
          </button>
        </div>
      </Modal>

      <ConvertConfirmModal
        open={showConvertConfirmModal}
        onCancel={() => {
          setShowConvertConfirmModal(false);
          pendingConvertRef.current = null;
          setConvertConfirmDirection("fiatToUsdt");
        }}
        direction={convertConfirmDirection}
        receiveCurrency={convertModalReceiveCurrency}
        sourceCurrency={convertConfirmDirection === "usdtToFiat" ? "USDT" : pendingConvertRef.current?.fiatCode || "USDT"}
        sourceAmount={convertModalSourceAmount}
        previewData={convertPreviewData}
        previewLoading={convertPreviewLoading}
        onConfirm={handleConvertConfirmSubmit}
        confirmDisabled={
          convertPreviewLoading ||
          !convertPreviewData?.targetAmount ||
          (!!submittingStrategyId && convertConfirmDirection === "fiatToUsdt")
        }
      />

      {/* 跟单/兑换失败错误提示；滑块余额为0或低于最低范围时同结构弹窗展示「去充值」 */}
      <Modal
        open={showFollowErrorModal}
        onCancel={() => {
          setShowFollowErrorModal(false);
          setFollowErrorIsInsufficientFunds(false);
        }}
        footer={null}
        closable={false}
        maskClosable={true}
        centered={true}
        width={343}
        zIndex={10051}
        className="successModal"
        maskStyle={{ background: "rgba(0, 0, 0, 0.4)" }}
      >
        <div className="modalContent">
          <div className="modalText">
            <div className="modalTitle" style={{ marginBottom: 8 }}>
              {followErrorModalMessage}
            </div>
          </div>
          {followErrorIsInsufficientFunds ? (
            <div style={{ display: "flex", gap: 12, marginTop: 20, width: "100%" }}>
              <button
                className="modalButton"
                style={{ flex: 1, background: "#24262B", color: "#fff" }}
                onClick={() => {
                  setShowFollowErrorModal(false);
                  setFollowErrorIsInsufficientFunds(false);
                }}
              >
                {t("aicopy.cancel") || "Cancel"}
              </button>
              <button
                className="modalButton"
                style={{ flex: 1 }}
                onClick={() => {
                  setShowFollowErrorModal(false);
                  setFollowErrorIsInsufficientFunds(false);
                  if (isAicopyVoucherOrderOption(followErrorDepositCurrency)) {
                    window.location.href = getUrl("/activity/voucher");
                  } else {
                    const isFiat =
                      !isAicopyUsdt(followErrorDepositCurrency) && !isAicopyUsdc(followErrorDepositCurrency);
                    window.location.href = getUrl(isFiat ? "/wallet/account/common/deposit/fiat" : "/wallet/account/common/deposit");
                  }
                }}
              >
                {t("aicopy.goDeposit") || "Deposit"}
              </button>
            </div>
          ) : (
            <button className="modalButton" style={{ marginTop: 20, width: "100%" }} onClick={() => setShowFollowErrorModal(false)}>
              {t("aicopy.ok")}
            </button>
          )}
        </div>
      </Modal>
    </div>
  );
}

export default AiCopy;
